<template>
  <div class="admin-page">
    <!-- Header fijo -->
    <div class="admin-header-fixed">
      <div class="header-top">
        <h1 class="page-title">Control Horario</h1>
        <div class="right-section">
          <div class="profile-dropdown" @click="toggleProfileMenu">
            <span class="profile-dropdown__text">Mi perfil</span>
            <v-icon class="profile-dropdown__arrow" small>mdi-menu-down</v-icon>
            <div v-if="showProfileMenu" class="profile-dropdown__menu">
              <div class="profile-dropdown__item">Ausencias</div>
              <div class="profile-dropdown__item">Historial</div>
              <div class="profile-dropdown__item">Vacaciones</div>
              <div class="profile-dropdown__item">Balance mensual</div>
              <div class="profile-dropdown__item" @click.stop="goToMain">PARA ATRAS</div>
            </div>
          </div>
          <button class="btn-administrador">
            Administrador
          </button>
        </div>
      </div>
      <hr class="title-separator" />
      <div class="header-buttons">
        <button
          v-if="is_admin && !is_owner"
          class="btn-secundario-1"
          @click="openUserDialog(null)"
        >
          Crear Nuevo Usuario
        </button>
        <button
          v-if="is_owner"
          :class="['btn-secundario-1', { 'btn-secundario-1--pressed': currentView === 'users' }]"
          @click="showAdminAccessInfo"
        >
          Gestión de empleados
        </button>
        <button
          :class="['btn-secundario-1', { 'btn-secundario-1--pressed': absence_reasons_dialog }]"
          @click="openAbsenceReasonsDialog"
          :disabled="!can_edit_absence_reasons"
        >
          Configuración de Ausencias
        </button>
        <button
          :class="['btn-secundario-1', { 'btn-secundario-1--pressed': currentView === 'approvals' }]"
          @click="openSolicitudesDialog"
          :disabled="!can_edit_absence_reasons"
        >
          Centro de aprobaciones
        </button>
        <button
          v-if="is_admin || is_owner"
          :class="['btn-secundario-1', { 'btn-secundario-1--pressed': currentView === 'registro-jornada' }]"
          @click="openRegistroJornada"
        >
          Registro de Jornada
        </button>
      </div>
      <hr class="header-separator" />
      <!-- Selector de periodo + acciones para Registro de Jornada -->
      <div v-if="currentView === 'registro-jornada'" class="period-bar">
        <div class="period-bar__left">
          <div class="period-selector">
            <button
              v-for="period in periodOptions"
              :key="period.value"
              :class="['period-selector__btn', { 'period-selector__btn--active': rj_selectedPeriod === period.value }]"
              @click="rj_selectedPeriod = period.value"
            >
              {{ period.label }}
            </button>
          </div>
          <span class="date-display">{{ rj_currentDateFull }}</span>
          <div class="date-navigator">
            <button class="date-navigator__arrow" @click="rjPrevDate">
              <v-icon small>mdi-chevron-left</v-icon>
            </button>
            <span class="date-navigator__label">{{ rj_currentDateLabel }}</span>
            <button class="date-navigator__arrow" @click="rjNextDate">
              <v-icon small>mdi-chevron-right</v-icon>
            </button>
          </div>
        </div>
        <div class="period-bar__actions">
          <button
            :class="['btn-secundario-1', { 'btn-secundario-1--disabled': rj_checkedUsers.length !== 1 }]"
            :disabled="rj_checkedUsers.length !== 1"
            @click="rjOpenEditDayPopup"
          >
            <v-icon small class="mr-1">mdi-pencil-outline</v-icon>
            Editar fichaje
          </button>
          <button
            :class="['btn-secundario-1', { 'btn-secundario-1--disabled': rj_checkedUsers.length === 0 }]"
            :disabled="rj_checkedUsers.length === 0"
            @click="rjDownloadExcel"
          >
            <v-icon small class="mr-1">mdi-download</v-icon>
            Descargar
          </button>
        </div>
      </div>
      <hr v-if="currentView === 'registro-jornada'" class="header-separator" />
    </div>
    <div class="content-area">
      <nav v-if="currentView === 'approvals'" class="admin-sidebar">
        <div class="admin-sidebar__section">
          <ul class="admin-sidebar__list">
            <li v-for="item in sidebarItems" :key="item.id">
              <button
                :class="['admin-sidebar__item', { 'admin-sidebar__item--active': activeSection === item.id, 'admin-sidebar__item--child': item.icon || item.svgIcon }]"
                @click="toggleSidebarFilter(item.id)"
              >
                <v-icon v-if="item.icon" class="admin-sidebar__icon">{{ item.icon }}</v-icon>
                <img v-else-if="item.svgIcon" :src="item.svgIcon" class="admin-sidebar__svg-icon" />
                <span class="admin-sidebar__label">
                  {{ item.label }}
                  <span v-if="item.count !== undefined" class="admin-sidebar__count">({{ item.count }})</span>
                </span>
                <span v-if="item.badge" class="admin-sidebar__badge">{{ item.badge }}</span>
              </button>
            </li>
          </ul>
        </div>
      </nav>
      <div class="admin-content">
        <div class="data-table-container">
          <!-- Tabla de usuarios -->
          <v-data-table
            v-if="currentView === 'users'"
            :headers="headers"
            :items="users"
            :loading="ui_loading"
            loading-text="Cargando usuarios..."
            no-data-text="No hay usuarios para mostrar."
            :items-per-page="10"
            :item-class="getRowClass"
            class="flat-table"
            disable-sort
            :footer-props="{ 'items-per-page-text': 'Filas por página' }"
          >
            <template v-slot:item.actions="{ item }">
              <!-- Para OWNERS: Solo editar horario y ver previsualización -->
              <template v-if="is_owner">
                <v-icon
                  small
                  class="mr-2"
                  @click="openWorkScheduleDialog(item)"
                  color="primary"
                  title="Editar horario de trabajo"
                >
                  mdi-clock-edit
                </v-icon>
                <v-icon
                  small
                  class="mr-2"
                  @click="goToMonthPreview(item)"
                  color="primary"
                  title="Ver previsualización del mes"
                >
                  mdi-calendar-month
                </v-icon>
              </template>

              <!-- Para ADMINS (que no son owners): Todas las acciones -->
              <template v-else-if="is_admin && !is_owner">
                <v-icon
                  small
                  class="mr-2"
                  @click="openUserDialog(item)"
                  :disabled="!is_admin && item.id !== userData.id"
                  title="Editar usuario"
                >
                  mdi-pencil
                </v-icon>
                <v-icon
                  small
                  class="mr-2"
                  @click="openChangePasswordDialog(item)"
                  :disabled="!is_admin || item.is_owner"
                  title="Cambiar contraseña"
                >
                  mdi-lock-reset
                </v-icon>
                <v-icon
                  small
                  class="mr-2"
                  @click="goToMonthPreview(item)"
                  color="primary"
                  title="Ver previsualización del mes"
                >
                  mdi-calendar-month
                </v-icon>
                <v-icon
                  small
                  class="mr-2"
                  @click="toggleUserActive(item)"
                  :disabled="!is_admin || item.is_owner || item.id === userData.id"
                  :color="item.is_active ? 'error' : 'success'"
                  :title="item.is_active ? 'Desactivar usuario' : 'Activar usuario'"
                >
                  {{ item.is_active ? 'mdi-account-cancel' : 'mdi-account-check' }}
                </v-icon>
              </template>
            </template>
          </v-data-table>

          <!-- Tabla de aprobaciones pendientes -->
          <v-data-table
            v-else-if="currentView === 'approvals' && activeSection !== 'historial'"
            :headers="approvalsHeaders"
            :items="filteredApprovals"
            :loading="ui_loading"
            loading-text="Cargando solicitudes..."
            no-data-text="No hay solicitudes pendientes de aprobación."
            :items-per-page="10"
            class="flat-table"
            disable-sort
            :footer-props="{ 'items-per-page-text': 'Filas por página' }"
          >
            <template v-slot:item.empleado_display="{ item }">
              <div class="employee-cell">
                <span>{{ item.empleado_display }}</span>
                <img :src="require('@/assets/account_circle.svg')" class="employee-icon" />
              </div>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="approval-actions">
                <button class="btn-reject" @click="rejectChange(item)">
                  Rechazar
                </button>
                <button class="btn-approve" @click="approveChange(item)">
                  {{ (item.solicitud_tipo === 'clock' && item.change_index === 0) ? 'Crear fichaje' : 'Aprobar cambio' }}
                </button>
              </div>
            </template>
          </v-data-table>

          <div v-if="currentView === 'approvals' && activeSection === 'historial'" class="history-section">
            <div class="history-filters-bar">
              <div class="history-filters-bar__left">
                <div class="history-search-container">
                  <button
                    class="history-search-btn"
                    @click="toggleHistorySearch"
                    :class="{ 'history-search-btn--active': historySearchExpanded }"
                  >
                    <img :src="require('@/assets/search.svg')" class="history-search-btn__icon" />
                  </button>
                  <transition name="search-expand">
                    <div v-if="historySearchExpanded" class="history-search-input">
                      <input
                        ref="historySearchInput"
                        type="text"
                        v-model="historySearchQuery"
                        placeholder="Buscar en historial..."
                        class="history-search-input__field"
                        @blur="onSearchBlur"
                        @keyup.escape="closeHistorySearch"
                      />
                      <button
                        v-if="historySearchQuery"
                        class="history-search-input__clear"
                        @click="historySearchQuery = ''"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </button>
                    </div>
                  </transition>
                </div>
              </div>
              <div class="history-filters-bar__right">
                <!-- Chips de filtros activos -->
                <v-chip
                  v-if="historyFilters.tipo"
                  close
                  @click:close="removeHistoryFilter('tipo')"
                  class="history-chip history-chip--active"
                >
                  Tipo: {{ historyFilters.tipo }}
                </v-chip>
                <v-chip
                  v-if="historyFilters.fecha_desde || historyFilters.fecha_hasta"
                  close
                  @click:close="removeHistoryFilter('fecha')"
                  class="history-chip history-chip--active"
                >
                  Fecha: {{ formatFilterDateRange }}
                </v-chip>

                <!-- Menú Añadir filtro -->
                <v-menu offset-y :close-on-content-click="false" v-model="historyFilterMenu">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip v-bind="attrs" v-on="on" class="history-chip">
                      Añadir filtro
                      <v-icon right small>mdi-plus</v-icon>
                    </v-chip>
                  </template>
                  <v-card class="history-filter-menu">
                    <v-list dense>
                      <!-- Filtro por Tipo -->
                      <v-list-group no-action>
                        <template v-slot:activator>
                          <v-list-item-icon>
                            <v-icon>mdi-tag-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>Tipo de cambio</v-list-item-title>
                          </v-list-item-content>
                        </template>
                        <v-list-item
                          v-for="tipo in tiposCambioOptions"
                          :key="tipo.value"
                          @click="applyHistoryFilter('tipo', tipo.value)"
                        >
                          <v-list-item-title class="pl-4">{{ tipo.text }}</v-list-item-title>
                        </v-list-item>
                      </v-list-group>

                      <!-- Filtro por Fecha -->
                      <v-list-group no-action>
                        <template v-slot:activator>
                          <v-list-item-icon>
                            <v-icon>mdi-calendar-range</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>Fecha</v-list-item-title>
                          </v-list-item-content>
                        </template>
                        <v-list-item class="px-4">
                          <v-row>
                            <v-col cols="6">
                              <v-menu
                                v-model="menuFechaDesde"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    v-model="historyFilters.fecha_desde"
                                    label="Desde"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    dense
                                    v-bind="attrs"
                                    v-on="on"
                                  ></v-text-field>
                                </template>
                                <v-date-picker
                                  v-model="historyFilters.fecha_desde"
                                  @input="menuFechaDesde = false"
                                  locale="es"
                                ></v-date-picker>
                              </v-menu>
                            </v-col>
                            <v-col cols="6">
                              <v-menu
                                v-model="menuFechaHasta"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    v-model="historyFilters.fecha_hasta"
                                    label="Hasta"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    dense
                                    v-bind="attrs"
                                    v-on="on"
                                  ></v-text-field>
                                </template>
                                <v-date-picker
                                  v-model="historyFilters.fecha_hasta"
                                  @input="menuFechaHasta = false"
                                  locale="es"
                                ></v-date-picker>
                              </v-menu>
                            </v-col>
                          </v-row>
                        </v-list-item>
                      </v-list-group>
                    </v-list>
                  </v-card>
                </v-menu>

                <!-- Borrar filtros -->
                <v-chip
                  v-if="hasActiveHistoryFilters"
                  @click="clearHistoryFilters"
                  class="history-chip history-chip--clear"
                >
                  Borrar filtros
                  <img :src="require('@/assets/delete-filters.svg')" class="history-chip__icon" />
                </v-chip>
              </div>
            </div>

            <!-- Tabla de historial -->
            <v-data-table
              :headers="historyHeaders"
              :items="filteredHistoryItems"
              :loading="history_loading"
              loading-text="Cargando historial..."
              no-data-text="No hay solicitudes procesadas en el historial."
              :items-per-page="10"
              class="flat-table"
              disable-sort
              :footer-props="{ 'items-per-page-text': 'Filas por página' }"
            >
              <template v-slot:item.empleado_display="{ item }">
                <div class="employee-cell">
                  <span>{{ item.empleado_display }}</span>
                  <img :src="require('@/assets/account_circle.svg')" class="employee-icon" />
                </div>
              </template>
              <template v-slot:item.estado="{ item }">
                <v-chip
                  :color="item.estado === 'Aprobado' ? 'success' : 'error'"
                  small
                  class="status-chip"
                >
                  {{ item.estado }}
                </v-chip>
              </template>
              <template v-slot:item.fecha_resolucion="{ item }">
                <span>{{ item.fecha_resolucion || '-' }}</span>
              </template>
            </v-data-table>
          </div>

          <div v-if="currentView === 'registro-jornada'" class="registro-jornada-view">
            <div v-if="!rj_selectedEmployee && (rj_selectedPeriod === 'dia' || rj_selectedPeriod === 'semana')" class="rj-employees-list">
              <v-data-table
                :headers="rjEmployeeHeaders"
                :items="rjSortedUsers"
                :loading="rj_selectedPeriod === 'semana' ? rj_weekProgressLoading : rj_progressLoading"
                loading-text="Cargando datos de jornada..."
                no-data-text="No hay empleados para mostrar."
                :items-per-page="10"
                class="flat-table rj-table"
                disable-sort
                hide-default-header
                :footer-props="{ 'items-per-page-text': 'Filas por página' }"
                @click:row="rjSelectEmployee"
              >
                <template v-slot:header>
                  <thead v-if="rj_selectedPeriod === 'semana'">
                    <tr class="rj-header-row">
                      <th class="rj-header-cell rj-header-cell--empleado rj-header-cell--bordered">
                        <div class="rj-table-employee">
                          <v-simple-checkbox
                            :value="rj_checkedUsers.length === users.length && users.length > 0"
                            :indeterminate="rj_checkedUsers.length > 0 && rj_checkedUsers.length < users.length"
                            @input="rjToggleCheckAll"
                            color="#7339B7"
                            class="rj-table-checkbox"
                          ></v-simple-checkbox>
                          <span>Empleado</span>
                        </div>
                      </th>
                      <th class="rj-header-cell rj-header-cell--bordered">Estado</th>
                      <th class="rj-header-cell text-center rj-header-cell--bordered" colspan="9">Horas por día</th>
                      <th class="rj-header-cell text-center">Acciones</th>
                    </tr>
                    <tr class="rj-subheader-row">
                      <th class="rj-subheader-cell rj-subheader-cell--empleado">
                        <div class="rj-subheader-empleado-icons">
                          <img
                            :src="require('@/assets/icon_alphabetic.svg')"
                            :class="['rj-subheader-icon', { 'rj-subheader-icon--active': rj_alphabeticOrder }]"
                            @click="rj_alphabeticOrder = !rj_alphabeticOrder"
                          />
                          <img
                            :src="require('@/assets/icon_invertir_orden.svg')"
                            :class="['rj-subheader-icon', { 'rj-subheader-icon--active': rj_invertedOrder }]"
                            @click="rj_invertedOrder = !rj_invertedOrder"
                          />
                        </div>
                      </th>
                      <th class="rj-subheader-cell"></th>
                      <th class="rj-subheader-cell text-center">Lunes</th>
                      <th class="rj-subheader-cell text-center">Martes</th>
                      <th class="rj-subheader-cell text-center">Miércoles</th>
                      <th class="rj-subheader-cell text-center">Jueves</th>
                      <th class="rj-subheader-cell text-center">Viernes</th>
                      <th class="rj-subheader-cell text-center">Sábado</th>
                      <th class="rj-subheader-cell text-center">Domingo</th>
                      <th class="rj-subheader-cell text-center">Hora Total Semanal</th>
                      <th class="rj-subheader-cell text-center">Estado Cierre</th>
                      <th class="rj-subheader-cell text-center">Acciones</th>
                    </tr>
                  </thead>
                  <thead v-else>
                    <tr class="rj-header-row">
                      <th class="rj-header-cell rj-header-cell--empleado rj-header-cell--bordered">
                        <div class="rj-table-employee">
                          <v-simple-checkbox
                            :value="rj_checkedUsers.length === users.length && users.length > 0"
                            :indeterminate="rj_checkedUsers.length > 0 && rj_checkedUsers.length < users.length"
                            @input="rjToggleCheckAll"
                            color="#7339B7"
                            class="rj-table-checkbox"
                          ></v-simple-checkbox>
                          <span>Empleado</span>
                        </div>
                      </th>
                      <th class="rj-header-cell rj-header-cell--bordered">Estado</th>
                      <th class="rj-header-cell text-center rj-header-cell--bordered" colspan="6">Balance</th>
                      <th class="rj-header-cell text-center">Acciones</th>
                    </tr>
                    <tr class="rj-subheader-row">
                      <th class="rj-subheader-cell rj-subheader-cell--empleado">
                        <div class="rj-subheader-empleado-icons">
                          <img
                            :src="require('@/assets/icon_alphabetic.svg')"
                            :class="['rj-subheader-icon', { 'rj-subheader-icon--active': rj_alphabeticOrder }]"
                            @click="rj_alphabeticOrder = !rj_alphabeticOrder"
                          />
                          <img
                            :src="require('@/assets/icon_invertir_orden.svg')"
                            :class="['rj-subheader-icon', { 'rj-subheader-icon--active': rj_invertedOrder }]"
                            @click="rj_invertedOrder = !rj_invertedOrder"
                          />
                        </div>
                      </th>
                      <th class="rj-subheader-cell"></th>
                      <th class="rj-subheader-cell text-center">Entrada</th>
                      <th class="rj-subheader-cell text-center">Pausa</th>
                      <th class="rj-subheader-cell text-center">Salida</th>
                      <th class="rj-subheader-cell text-center">Total</th>
                      <th class="rj-subheader-cell text-center">Balance</th>
                      <th class="rj-subheader-cell">Gráfico</th>
                      <th class="rj-subheader-cell text-center">Acciones</th>
                    </tr>
                  </thead>
                </template>
                <template v-slot:item.username="{ item }">
                  <div class="rj-table-employee">
                    <v-simple-checkbox
                      :value="rj_checkedUsers.includes(item.id)"
                      @input="rjToggleCheckUser(item.id)"
                      color="#7339B7"
                      class="rj-table-checkbox"
                    ></v-simple-checkbox>
                    <span>{{ item.displayName || item.username }}</span>
                  </div>
                </template>
                <template v-slot:item.estado="{ item }">
                  <v-chip
                    :color="rjGetUserEstado(item.id).color"
                    small
                    :class="['rj-status-chip', { 'rj-status-chip--completed': rjGetUserEstado(item.id).useSvg }]"
                    :dark="rjGetUserEstado(item.id).dark"
                  >
                    <img
                      v-if="rjGetUserEstado(item.id).useSvg"
                      :src="require('@/assets/' + rjGetUserEstado(item.id).svgAsset)"
                      class="rj-status-chip__svg"
                    />
                    <v-icon v-else left small>{{ rjGetUserEstado(item.id).icon }}</v-icon>
                    {{ rjGetUserEstado(item.id).text }}
                  </v-chip>
                </template>
                <template v-slot:item.entrada="{ item }">
                  <span class="rj-cell-value">{{ rjGetUserDayInfo(item.id).entrada }}</span>
                </template>
                <template v-slot:item.pausa="{ item }">
                  <span class="rj-cell-value">{{ rjGetUserDayInfo(item.id).pausaRango }}</span>
                </template>
                <template v-slot:item.salida="{ item }">
                  <span class="rj-cell-value">{{ rjGetUserDayInfo(item.id).salida }}</span>
                </template>
                <template v-slot:item.total="{ item }">
                  <span class="rj-cell-value rj-cell-value--total">{{ rjGetUserDayInfo(item.id).total }}</span>
                </template>
                <template v-slot:item.balance="{ item }">
                  <span
                    class="rj-cell-value"
                    :class="{
                      'rj-balance-positive': rjGetUserBalance(item.id).diffSecs > 0,
                      'rj-balance-negative': rjGetUserBalance(item.id).diffSecs < 0,
                      'rj-balance-zero': rjGetUserBalance(item.id).diffSecs === 0
                    }"
                  >
                    {{ rjGetUserBalance(item.id).formatted }}
                  </span>
                </template>
                <template v-slot:item.grafico="{ item }">
                  <div class="rj-table-balance__progress" v-if="rj_activeProgressData[item.id]">
                    <div class="rj-progress-bar-track">
                      <div
                        v-for="(segment, idx) in rjGetUserProgressSegments(item.id)"
                        :key="idx"
                        class="rj-progress-bar-segment"
                        :class="{
                          'rj-segment-work': segment.type === 'work' && !rjGetUserProgress(item.id).isCompleted,
                          'rj-segment-pause': segment.type === 'pause' && !rjGetUserProgress(item.id).isCompleted,
                          'rj-segment-work-completed': segment.type === 'work' && rjGetUserProgress(item.id).isCompleted,
                          'rj-segment-pause-completed': segment.type === 'pause' && rjGetUserProgress(item.id).isCompleted,
                          'rj-segment-active': segment.active
                        }"
                        :style="{ width: segment.width + '%', left: segment.left + '%' }"
                      ></div>
                    </div>
                  </div>
                </template>
                <template v-slot:item.lunes="{ item }">
                  <span class="rj-cell-value">{{ rjGetUserWeekDayHours(item.id, 0) }}</span>
                </template>
                <template v-slot:item.martes="{ item }">
                  <span class="rj-cell-value">{{ rjGetUserWeekDayHours(item.id, 1) }}</span>
                </template>
                <template v-slot:item.miercoles="{ item }">
                  <span class="rj-cell-value">{{ rjGetUserWeekDayHours(item.id, 2) }}</span>
                </template>
                <template v-slot:item.jueves="{ item }">
                  <span class="rj-cell-value">{{ rjGetUserWeekDayHours(item.id, 3) }}</span>
                </template>
                <template v-slot:item.viernes="{ item }">
                  <span class="rj-cell-value">{{ rjGetUserWeekDayHours(item.id, 4) }}</span>
                </template>
                <template v-slot:item.sabado="{ item }">
                  <span class="rj-cell-value">{{ rjGetUserWeekDayHours(item.id, 5) }}</span>
                </template>
                <template v-slot:item.domingo="{ item }">
                  <span class="rj-cell-value">{{ rjGetUserWeekDayHours(item.id, 6) }}</span>
                </template>
                <template v-slot:item.totalSemanal="{ item }">
                  <span class="rj-cell-value rj-cell-value--total">{{ rjGetUserWeekTotalHours(item.id) }}</span>
                </template>
                <template v-slot:item.estadoCierre="{ item }">
                  <span
                    class="rj-estado-cierre"
                    :style="{ backgroundColor: rjGetUserWeekClosureStatus(item.id).bg }"
                  >
                    <img
                      v-if="rjGetUserWeekClosureStatus(item.id).icon"
                      :src="rjGetUserWeekClosureStatus(item.id).icon"
                      class="rj-estado-cierre__icon"
                    />
                    {{ rjGetUserWeekClosureStatus(item.id).text }}
                  </span>
                </template>
                <template v-slot:item.acciones="{ item }">
                  <div class="rj-acciones-cell">
                    <img
                      :src="require('@/assets/icon_user.svg')"
                      class="rj-acciones-icon"
                    />
                    <v-btn
                      small
                      class="rj-acciones-gestionar-btn"
                      @click.stop="rjSelectEmployee(item)"
                    >
                      Gestionar
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </div>
            <div v-if="!rj_selectedEmployee && rj_selectedPeriod === 'mes'" class="rj-employees-list rj-employees-list--mes">
              <v-data-table
                :headers="rjMonthEmployeeHeaders"
                :items="rjSortedUsers"
                :loading="rj_monthProgressLoading"
                loading-text="Cargando datos de jornada..."
                no-data-text="No hay empleados para mostrar."
                :items-per-page="10"
                class="flat-table rj-table rj-table--mes"
                disable-sort
                hide-default-header
                :footer-props="{ 'items-per-page-text': 'Filas por página' }"
                @click:row="rjSelectEmployee"
              >
                <template v-slot:header>
                  <thead>
                    <tr class="rj-header-row">
                      <th class="rj-header-cell rj-header-cell--empleado rj-header-cell--bordered">
                        <div class="rj-table-employee">
                          <v-simple-checkbox
                            :value="rj_checkedUsers.length === users.length && users.length > 0"
                            :indeterminate="rj_checkedUsers.length > 0 && rj_checkedUsers.length < users.length"
                            @input="rjToggleCheckAll"
                            color="#7339B7"
                            class="rj-table-checkbox"
                          ></v-simple-checkbox>
                          <span>Empleado</span>
                        </div>
                      </th>
                      <th class="rj-header-cell text-center rj-header-cell--bordered" :colspan="rjMonthDays.length + 4">Balance</th>
                      <th class="rj-header-cell text-center">Acciones</th>
                    </tr>
                    <tr class="rj-subheader-row">
                      <th class="rj-subheader-cell rj-subheader-cell--empleado">
                        <div class="rj-subheader-empleado-icons">
                          <img
                            :src="require('@/assets/icon_alphabetic.svg')"
                            :class="['rj-subheader-icon', { 'rj-subheader-icon--active': rj_alphabeticOrder }]"
                            @click="rj_alphabeticOrder = !rj_alphabeticOrder"
                          />
                          <img
                            :src="require('@/assets/icon_invertir_orden.svg')"
                            :class="['rj-subheader-icon', { 'rj-subheader-icon--active': rj_invertedOrder }]"
                            @click="rj_invertedOrder = !rj_invertedOrder"
                          />
                        </div>
                      </th>
                      <th
                        v-for="day in rjMonthDays"
                        :key="'mh-' + day.dayNumber"
                        class="rj-subheader-cell text-center rj-mes-day-header"
                        :class="{ 'rj-mes-day-header--weekend': day.isWeekend }"
                      >
                        <div class="rj-mes-day-indicator">
                          <span class="rj-mes-day-number">{{ day.dayNumber }}</span>
                          <span class="rj-mes-day-letter">{{ day.dayLetter }}</span>
                        </div>
                      </th>
                      <th class="rj-subheader-cell text-center rj-mes-summary-header">Hora Total Mensual</th>
                      <th class="rj-subheader-cell text-center rj-mes-summary-header">Vacaciones</th>
                      <th class="rj-subheader-cell text-center rj-mes-summary-header">Ausencias</th>
                      <th class="rj-subheader-cell text-center rj-mes-summary-header">Estado Cierre Prev.</th>
                      <th class="rj-subheader-cell text-center rj-mes-summary-header">Acciones</th>
                    </tr>
                  </thead>
                </template>
                <template v-slot:body="{ items }">
                  <tbody>
                    <tr v-for="item in items" :key="item.id" @click="rjSelectEmployee(item)">
                      <td class="rj-mes-cell rj-mes-cell--empleado rj-mes-sticky-col rj-mes-sticky-col--empleado">
                        <div class="rj-table-employee">
                          <v-simple-checkbox
                            :value="rj_checkedUsers.includes(item.id)"
                            @input="rjToggleCheckUser(item.id)"
                            color="#7339B7"
                            class="rj-table-checkbox"
                          ></v-simple-checkbox>
                          <span>{{ item.displayName || item.username }}</span>
                        </div>
                      </td>
                      <td
                        v-for="day in rjMonthDays"
                        :key="'md-' + day.dayNumber"
                        class="rj-mes-cell rj-mes-day-cell text-center"
                        :class="{ 'rj-mes-day-cell--weekend': day.isWeekend }"
                      >
                        <img
                          v-if="rjGetMonthDayStatus(item.id, day.dayNumber) === 'completed'"
                          :src="require('@/assets/tic_icon.svg')"
                          class="rj-mes-day-icon"
                        />
                        <img
                          v-else-if="rjGetMonthDayStatus(item.id, day.dayNumber) === 'incomplete'"
                          :src="require('@/assets/warning_icon.svg')"
                          class="rj-mes-day-icon"
                        />
                        <span v-else class="rj-mes-day-value">-</span>
                      </td>
                      <td class="rj-mes-cell text-center">
                        <span
                          class="rj-cell-value rj-cell-value--total"
                          :class="{ 'rj-mes-total--deficit': rjGetMonthTotal(item.id).deficit }"
                        >{{ rjGetMonthTotal(item.id).label }}</span>
                      </td>
                      <td class="rj-mes-cell text-center">
                        <span class="rj-cell-value">-</span>
                      </td>
                      <td class="rj-mes-cell text-center">
                        <span class="rj-cell-value">-</span>
                      </td>
                      <td class="rj-mes-cell text-center">
                        <span
                          v-if="rjGetMonthTotal(item.id).completed"
                          class="rj-mes-estado-cierre rj-mes-estado-cierre--correcto"
                        >
                          <img :src="require('@/assets/tic_icon.svg')" class="rj-mes-estado-cierre__icon" />
                          Correcto
                        </span>
                        <span
                          v-else-if="rjGetMonthTotal(item.id).deficit"
                          class="rj-mes-estado-cierre rj-mes-estado-cierre--incorrecto"
                        >
                          <img :src="require('@/assets/warning_icon.svg')" class="rj-mes-estado-cierre__icon" />
                          Incorrecto
                        </span>
                        <span v-else class="rj-cell-value">-</span>
                      </td>
                      <td class="rj-mes-cell text-center">
                        <div class="rj-acciones-cell">
                          <img
                            :src="require('@/assets/icon_user.svg')"
                            class="rj-acciones-icon"
                          />
                          <v-btn
                            small
                            class="rj-acciones-gestionar-btn"
                            @click.stop="rjSelectEmployee(item)"
                          >
                            Gestionar
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-data-table>
            </div>
            <!-- ========== Vista Año ========== -->
            <div v-if="!rj_selectedEmployee && rj_selectedPeriod === 'ano'" class="rj-employees-list">
              <v-data-table
                :headers="rjYearEmployeeHeaders"
                :items="rjSortedUsers"
                :loading="rj_yearProgressLoading"
                loading-text="Cargando datos de jornada..."
                no-data-text="No hay empleados para mostrar."
                :items-per-page="10"
                class="flat-table rj-table"
                disable-sort
                hide-default-header
                :footer-props="{ 'items-per-page-text': 'Filas por página' }"
                @click:row="rjSelectEmployee"
              >
                <template v-slot:header>
                  <thead>
                    <tr class="rj-header-row">
                      <th class="rj-header-cell rj-header-cell--empleado rj-header-cell--bordered">
                        <div class="rj-table-employee">
                          <v-simple-checkbox
                            :value="rj_checkedUsers.length === users.length && users.length > 0"
                            :indeterminate="rj_checkedUsers.length > 0 && rj_checkedUsers.length < users.length"
                            @input="rjToggleCheckAll"
                            color="#7339B7"
                            class="rj-table-checkbox"
                          ></v-simple-checkbox>
                          <span>Empleado</span>
                        </div>
                      </th>
                      <th class="rj-header-cell text-center rj-header-cell--bordered" colspan="3">Balance Anual</th>
                      <th class="rj-header-cell text-center">Acciones</th>
                    </tr>
                    <tr class="rj-subheader-row">
                      <th class="rj-subheader-cell rj-subheader-cell--empleado">
                        <div class="rj-subheader-empleado-icons">
                          <img
                            :src="require('@/assets/icon_alphabetic.svg')"
                            :class="['rj-subheader-icon', { 'rj-subheader-icon--active': rj_alphabeticOrder }]"
                            @click="rj_alphabeticOrder = !rj_alphabeticOrder"
                          />
                          <img
                            :src="require('@/assets/icon_invertir_orden.svg')"
                            :class="['rj-subheader-icon', { 'rj-subheader-icon--active': rj_invertedOrder }]"
                            @click="rj_invertedOrder = !rj_invertedOrder"
                          />
                        </div>
                      </th>
                      <th class="rj-subheader-cell text-center">Contrato Anual</th>
                      <th class="rj-subheader-cell text-center">Horas trabajadas</th>
                      <th class="rj-subheader-cell text-center">Balance</th>
                      <th class="rj-subheader-cell text-center">Acciones</th>
                    </tr>
                  </thead>
                </template>
                <template v-slot:item.username="{ item }">
                  <div class="rj-table-employee">
                    <v-simple-checkbox
                      :value="rj_checkedUsers.includes(item.id)"
                      @input="rjToggleCheckUser(item.id)"
                      color="#7339B7"
                      class="rj-table-checkbox"
                    ></v-simple-checkbox>
                    <span>{{ item.displayName || item.username }}</span>
                  </div>
                </template>
                <template v-slot:item.contratoAnual="{ item }">
                  <span class="rj-cell-value">{{ rjGetYearContractLabel(item.id) }}</span>
                </template>
                <template v-slot:item.horasTrabajadas="{ item }">
                  <span class="rj-cell-value">{{ rjGetYearWorkedLabel(item.id) }}</span>
                </template>
                <template v-slot:item.balanceAnual="{ item }">
                  <span
                    class="rj-cell-value"
                    :class="{
                      'rj-balance-positive': rjGetYearBalance(item.id).secs > 0,
                      'rj-balance-negative': rjGetYearBalance(item.id).secs < 0,
                      'rj-balance-zero': rjGetYearBalance(item.id).secs === 0
                    }"
                  >
                    {{ rjGetYearBalance(item.id).formatted }}
                  </span>
                </template>
                <template v-slot:item.acciones="{ item }">
                  <div class="rj-acciones-cell">
                    <img
                      :src="require('@/assets/icon_user.svg')"
                      class="rj-acciones-icon"
                    />
                    <v-btn
                      small
                      class="rj-acciones-gestionar-btn"
                      @click.stop="rjSelectEmployee(item)"
                    >
                      Gestionar
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </div>
            <div v-if="rj_selectedEmployee" class="rj-employee-history">
              <!-- Header principal -->
              <div class="rj-detail-header">
                <div class="rj-detail-header__left" @click="rjBackToList">
                  <v-icon small class="rj-detail-header__arrow">mdi-arrow-left</v-icon>
                  <span class="rj-detail-header__back-text">VOLVER LISTADO</span>
                </div>
                <div class="rj-detail-header__center">
                  <span class="rj-detail-header__username">{{ rj_selectedEmployee.displayName || rj_selectedEmployee.username }}</span>
                </div>
                <div class="rj-detail-header__right">
                  <v-menu offset-y :close-on-content-click="false" v-model="rj_filterMenu">
                    <template v-slot:activator="{ on }">
                      <v-chip v-on="on" class="rj-detail-header__filter-chip">
                        Añadir filtro
                        <v-icon right small>mdi-plus</v-icon>
                      </v-chip>
                    </template>
                    <div class="rj-filter-menu">
                      <div class="rj-filter-menu__row">
                        <label class="rj-filter-menu__label">Desde:</label>
                        <input type="date" v-model="rj_filter.startDate" class="rj-date-input" />
                      </div>
                      <div class="rj-filter-menu__row">
                        <label class="rj-filter-menu__label">Hasta:</label>
                        <input type="date" v-model="rj_filter.endDate" class="rj-date-input" />
                      </div>
                      <div class="rj-filter-menu__actions">
                        <v-btn small text @click="rjClearFilter(); rj_filterMenu = false;">
                          Limpiar
                        </v-btn>
                        <v-btn small color="primary" @click="rjLoadClocks(); rj_filterMenu = false;" :disabled="rj_loading">
                          Aplicar
                        </v-btn>
                      </div>
                    </div>
                  </v-menu>
                  <v-btn small color="primary" @click="openSetClockPopUp" class="rj-detail-header__crear-btn ml-2">
                    Crear fichaje
                  </v-btn>
                </div>
              </div>
              <div v-if="rj_filter.startDate || rj_filter.endDate" class="rj-active-filters">
                <v-chip
                  v-if="rj_filter.startDate"
                  small
                  close
                  @click:close="rj_filter.startDate = ''; rjLoadClocks();"
                  class="rj-active-filters__chip"
                >
                  Desde: {{ rj_filter.startDate }}
                </v-chip>
                <v-chip
                  v-if="rj_filter.endDate"
                  small
                  close
                  @click:close="rj_filter.endDate = ''; rjLoadClocks();"
                  class="rj-active-filters__chip"
                >
                  Hasta: {{ rj_filter.endDate }}
                </v-chip>
              </div>

              <v-data-table
                :headers="rjClockDetailHeaders"
                :items="rjClockDailyHistory"
                :loading="rj_loading"
                loading-text="Cargando fichajes..."
                no-data-text="No hay fichajes registrados para este empleado en el rango seleccionado."
                :items-per-page="rj_selectedPeriod === 'dia' ? -1 : 10"
                class="flat-table rj-table rj-clocks-table"
                disable-sort
                hide-default-header
                :hide-default-footer="rj_selectedPeriod === 'dia'"
                :footer-props="{ 'items-per-page-text': 'Filas por página' }"
                item-key="dateStr"
              >
                <template v-slot:header>
                  <thead>
                    <tr class="rj-subheader-row">
                      <th class="rj-subheader-cell" style="background: #FFFFFF !important;"></th>
                      <th class="rj-subheader-cell text-center">Entrada</th>
                      <th class="rj-subheader-cell text-center">Pausa</th>
                      <th class="rj-subheader-cell text-center">Salida</th>
                      <th class="rj-subheader-cell text-center">Total</th>
                      <th class="rj-subheader-cell text-center">Balance</th>
                      <th class="rj-subheader-cell text-center">Gráfico</th>
                      <th class="rj-subheader-cell text-center">Estado</th>
                    </tr>
                  </thead>
                </template>
                <template v-slot:item.dateDisplay="{ item }">
                  <div class="rj-cell-date">
                    <span class="rj-cell-date__day">{{ item.dayNameLong }}</span>
                    <span class="rj-cell-date__num">{{ item.dateFormatted }}</span>
                  </div>
                </template>
                <template v-slot:item.entrada="{ item }">
                  <span class="rj-cell-value">{{ item.entrada }}</span>
                </template>
                <template v-slot:item.pausa="{ item }">
                  <span class="rj-cell-value">{{ item.pausa }}</span>
                </template>
                <template v-slot:item.salida="{ item }">
                  <span class="rj-cell-value">{{ item.salida }}</span>
                </template>
                <template v-slot:item.total="{ item }">
                  <span class="rj-cell-value rj-cell-value--total">{{ item.total }}</span>
                </template>
                <template v-slot:item.balance="{ item }">
                  <span
                    class="rj-cell-value"
                    :class="{
                      'rj-balance-positive': item.balanceDiffSecs > 0,
                      'rj-balance-negative': item.balanceDiffSecs < 0,
                      'rj-balance-zero': item.balanceDiffSecs === 0
                    }"
                  >
                    {{ item.balanceFormatted }}
                  </span>
                </template>
                <template v-slot:item.grafico="{ item }">
                  <div class="rj-table-balance__progress" v-if="item.segments && item.segments.length > 0">
                    <div class="rj-progress-bar-track">
                      <div
                        v-for="(segment, idx) in item.segments"
                        :key="idx"
                        class="rj-progress-bar-segment"
                        :class="{
                          'rj-segment-work': segment.type === 'work' && !item.isCompleted,
                          'rj-segment-pause': segment.type === 'pause' && !item.isCompleted,
                          'rj-segment-work-completed': segment.type === 'work' && item.isCompleted,
                          'rj-segment-pause-completed': segment.type === 'pause' && item.isCompleted,
                          'rj-segment-active': segment.active
                        }"
                        :style="{ width: segment.width + '%', left: segment.left + '%' }"
                      ></div>
                    </div>
                  </div>
                </template>
                <template v-slot:item.estado="{ item }">
                  <v-chip
                    color="transparent"
                    small
                    class="rj-status-chip rj-status-chip--completed"
                  >
                    <img
                      :src="require('@/assets/' + item.estado.svgAsset)"
                      class="rj-status-chip__svg"
                    />
                    {{ item.estado.text }}
                  </v-chip>
                </template>
              </v-data-table>

              <!-- Dialog detalle fichajes de un día -->
              <v-dialog v-model="rj_dayClockDetail.show" max-width="550">
                <v-card>
                  <v-card-title class="rj-popup-header">
                    <span class="white--text">Fichajes del {{ rj_dayClockDetail.date }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="rjCloseDayClocks"><v-icon>mdi-close</v-icon></v-btn>
                  </v-card-title>
                  <v-card-text class="pa-0">
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th class="text-center">Hora</th>
                          <th class="text-center">Tipo</th>
                          <th class="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="clock in rj_dayClockDetail.clocks" :key="clock.uuid">
                          <td class="text-center">{{ rjFormatHHMM(clock.timestamp) }}</td>
                          <td class="text-center">
                            <v-chip :color="rjGetTypeColor(clock.type)" text-color="white" small>
                              {{ rjFormatType(clock.type) }}
                            </v-chip>
                          </td>
                          <td class="text-center">
                            <v-btn x-small outlined color="primary" @click="rjCloseDayClocks(); rjOpenEditClock(clock);">
                              <v-icon x-small left>mdi-pencil</v-icon> Modificar
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card-text>
                </v-card>
              </v-dialog>

              <!-- ========== Panel Mensajes (bottom) ========== -->
              <div class="rj-messages-panel">
                <div class="rj-messages-panel__header">
                  <span class="rj-messages-panel__title">Mensajes</span>
                  <span class="rj-messages-panel__date">{{ rjMessageDateLabel }}</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    small
                    :disabled="!rj_message.body"
                    @click="rjSendMessage"
                  >
                    Enviar
                  </v-btn>
                </div>
                <div class="rj-messages-panel__body">
                  <div class="rj-messages-panel__col-left">
                    <div class="rj-messages-panel__field">
                      <label class="rj-messages-panel__label">De</label>
                      <v-text-field
                        value="Administrador"
                        dense
                        outlined
                        readonly
                        hide-details
                        class="rj-messages-panel__input"
                      />
                    </div>
                    <div class="rj-messages-panel__field">
                      <label class="rj-messages-panel__label">Mensaje rápido</label>
                      <v-select
                        v-model="rj_message.quickMessage"
                        :items="rjQuickMessages"
                        dense
                        outlined
                        hide-details
                        placeholder="Seleccionar mensaje..."
                        class="rj-messages-panel__input"
                        @change="rjApplyQuickMessage"
                      />
                    </div>
                  </div>
                  <div class="rj-messages-panel__col-right">
                    <div class="rj-messages-panel__field rj-messages-panel__field--full">
                      <label class="rj-messages-panel__label">Mensaje personalizado</label>
                      <v-textarea
                        v-model="rj_message.body"
                        dense
                        outlined
                        hide-details
                        rows="3"
                        placeholder="Escribe un mensaje..."
                        class="rj-messages-panel__textarea"
                      />
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <update-clock-popup
              v-if="rj_editClockPopup.show && rj_editClockPopup.clock"
              :show="rj_editClockPopup.show"
              :clock="rj_editClockPopup.clock"
              :errors="rj_editClockPopup.errors"
              :is-admin="true"
              @close="rjHandleEditClockClose"
            />
            
            <v-dialog v-model="rj_setClockPopup.show" max-width="500" persistent>
              <v-card>
                <v-card-title class="rj-popup-header">
                  <span class="white--text">Crear fichaje para {{ rj_selectedEmployee ? rj_selectedEmployee.username : '' }}</span>
                  <v-spacer></v-spacer>
                  <v-btn icon dark @click="rj_setClockPopup.show = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text class="pt-4">
                  <v-form ref="setClockForm" v-model="rj_setClockPopup.formValid">
                    <div class="rj-form-field">
                      <label class="rj-form-label">Fecha</label>
                      <v-menu
                        v-model="rj_setClockPopup.dateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="rj_setClockPopup.form.date"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            :rules="[v => !!v || 'La fecha es requerida']"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="rj_setClockPopup.form.date"
                          @input="rj_setClockPopup.dateMenu = false"
                          locale="es"
                        ></v-date-picker>
                      </v-menu>
                    </div>
                    <div class="rj-form-field">
                      <label class="rj-form-label">Hora</label>
                      <v-menu
                        v-model="rj_setClockPopup.timeMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="rj_setClockPopup.form.time"
                            prepend-icon="mdi-clock-outline"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            :rules="[v => !!v || 'La hora es requerida']"
                          ></v-text-field>
                        </template>
                        <v-time-picker
                          v-model="rj_setClockPopup.form.time"
                          @click:minute="rj_setClockPopup.timeMenu = false"
                          format="24hr"
                        ></v-time-picker>
                      </v-menu>
                    </div>
                    <div class="rj-form-field">
                      <label class="rj-form-label">Tipo de fichaje</label>
                      <v-select
                        v-model="rj_setClockPopup.form.type"
                        :items="[
                          { text: 'Entrada', value: 'in' },
                          { text: 'Salida', value: 'out' },
                          { text: 'Pausa', value: 'pause' },
                          { text: 'Reanudar', value: 'continue' }
                        ]"
                        outlined
                        dense
                        :rules="[v => !!v || 'El tipo es requerido']"
                      ></v-select>
                    </div>
                  </v-form>
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="rj_setClockPopup.show = false">Cancelar</v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    :disabled="!rj_setClockPopup.formValid || rj_setClockPopup.loading"
                    :loading="rj_setClockPopup.loading"
                    @click="rjSubmitSetClock"
                  >
                    <v-icon left small>mdi-check</v-icon>
                    Crear fichaje
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-snackbar
              v-model="rj_snackbar.visible"
              :color="rj_snackbar.color"
              :timeout="3000"
            >
              <b>{{ rj_snackbar.text }}</b>
            </v-snackbar>
          </div>
        </div>
      </div>

    <!-- Popup Editar Fichaje del día (desde lista de empleados) -->
    <v-dialog v-model="rj_editDayPopup.show" persistent max-width="520">
      <v-card class="rj-edit-day-card">
        <v-btn icon class="btn-cancelar-flotante" @click="rjCloseEditDayPopup">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <v-card-title style="padding: 16px 24px 8px;">
          <div class="headline">Editar Fichaje</div>
        </v-card-title>

        <hr class="header-separator" />

        <v-card-text style="padding: 16px 24px !important; flex: 1;">
          <div class="campo-label" style="font-size: 14px; margin-bottom: 16px;">{{ rj_editDayPopup.userName }} - {{ rj_currentDateFull }}</div>

          <div class="rj-edit-day-grid">
            <div class="rj-edit-day-cell">
              <div class="campo-label">Entrada</div>
              <div class="rj-edit-day-field">
                <v-menu v-model="rj_editDayPopup.entradaMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="rj_editDayPopup.entrada" placeholder="--:--" class="campo-redondeado" hide-details solo readonly v-bind="attrs" v-on="on"></v-text-field>
                  </template>
                  <v-time-picker v-if="rj_editDayPopup.entradaMenu" v-model="rj_editDayPopup.entrada" @click:minute="rj_editDayPopup.entradaMenu = false" format="24hr" full-width></v-time-picker>
                </v-menu>
                <span class="rj-edit-day-delete rj-edit-day-delete--hidden"></span>
              </div>
            </div>

            <div class="rj-edit-day-cell">
              <div class="campo-label">Salida</div>
              <div class="rj-edit-day-field">
                <v-menu v-model="rj_editDayPopup.salidaMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="rj_editDayPopup.salida" placeholder="--:--" class="campo-redondeado" hide-details solo readonly v-bind="attrs" v-on="on"></v-text-field>
                  </template>
                  <v-time-picker v-if="rj_editDayPopup.salidaMenu" v-model="rj_editDayPopup.salida" @click:minute="rj_editDayPopup.salidaMenu = false" format="24hr" full-width></v-time-picker>
                </v-menu>
                <span class="rj-edit-day-delete rj-edit-day-delete--hidden"></span>
              </div>
            </div>

            <div class="rj-edit-day-cell">
              <div class="campo-label">Inicio pausa</div>
              <div class="rj-edit-day-field">
                <v-menu v-model="rj_editDayPopup.pausaInicioMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="rj_editDayPopup.pausaInicio" placeholder="--:--" class="campo-redondeado" hide-details solo readonly v-bind="attrs" v-on="on"></v-text-field>
                  </template>
                  <v-time-picker v-if="rj_editDayPopup.pausaInicioMenu" v-model="rj_editDayPopup.pausaInicio" @click:minute="rj_editDayPopup.pausaInicioMenu = false" format="24hr" full-width></v-time-picker>
                </v-menu>
                <v-btn icon small class="rj-edit-day-delete" :class="{ 'rj-edit-day-delete--hidden': !rj_editDayPopup.pausaInicio }" @click="rj_editDayPopup.pausaInicio = ''">
                  <v-icon small color="#B00020">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>

            <div class="rj-edit-day-cell">
              <div class="campo-label">Fin pausa</div>
              <div class="rj-edit-day-field">
                <v-menu v-model="rj_editDayPopup.pausaFinMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="rj_editDayPopup.pausaFin" placeholder="--:--" class="campo-redondeado" hide-details solo readonly v-bind="attrs" v-on="on"></v-text-field>
                  </template>
                  <v-time-picker v-if="rj_editDayPopup.pausaFinMenu" v-model="rj_editDayPopup.pausaFin" @click:minute="rj_editDayPopup.pausaFinMenu = false" format="24hr" full-width></v-time-picker>
                </v-menu>
                <v-btn icon small class="rj-edit-day-delete" :class="{ 'rj-edit-day-delete--hidden': !rj_editDayPopup.pausaFin }" @click="rj_editDayPopup.pausaFin = ''">
                  <v-icon small color="#B00020">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions style="padding: 12px 24px !important; justify-content: flex-end !important;">
          <v-btn class="btn-fondo1" @click="rjCloseEditDayPopup">Cancelar</v-btn>
          <v-btn
            class="btn-fondo"
            :loading="rj_editDayPopup.loading"
            :disabled="rj_editDayPopup.loading"
            @click="rjSaveEditDayPopup"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog_visible" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ getTitleText() }}</span>
        </v-card-title>

        <div class="subtitle-vicky">
          Este usuario {{ editing_user ? 'pertenece' : 'pertenecerá' }} a la empresa:<br>
          {{ companyName }}
        </div>

        <v-card-text>
          <v-container>
            <v-form ref="userForm">
              <!-- Campos de usuario (solo si NO es owner) -->
              <template v-if="!is_owner">
                <v-text-field
                  v-model="form_data.username"
                  label="Username"
                  :rules="[v => !!v || 'Username es obligatorio']"
                  :disabled="editing_user"
                  required
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form_data.email"
                  label="Correo Electrónico"
                  :rules="[
                    v => !!v || 'Email es obligatorio',
                    v => /.+@.+\..+/.test(v) || 'Email debe ser válido',
                  ]"
                  required
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-if="!editing_user"
                  v-model="form_data.password"
                  label="Contraseña"
                  type="password"
                  :rules="[
                    v => !!v || 'Contraseña es obligatoria',
                    v => v.length >= 8 || 'Mínimo 8 caracteres'
                  ]"
                  required
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-if="!editing_user"
                  v-model="form_data.password_repeat"
                  label="Repetir Contraseña"
                  type="password"
                  :rules="[
                    v => !!v || 'Repetir contraseña es obligatorio',
                    v => v === form_data.password || 'Las contraseñas no coinciden'
                  ]"
                  required
                  outlined
                  dense
                ></v-text-field>
              </template>

              <!-- Acceso a fichajes -->
              <v-divider class="my-4"></v-divider>
              <v-checkbox
                v-model="form_data.view_working_hours"
                label="Acceso a fichajes"
                class="mb-2"
              ></v-checkbox>

              <div v-if="form_data.view_working_hours">
                <h3 class="mb-3">Horario de Trabajo</h3>

                <v-text-field
                  v-model="form_data.work_schedule.nombre_jornada"
                  label="Nombre de la Jornada"
                  placeholder="Ej: Jornada Completa"
                  :rules="[v => !!v || 'Nombre de jornada es obligatorio']"
                  required
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model.number="form_data.work_schedule.vacation_days"
                  label="Días de Vacaciones al Año"
                  type="number"
                  placeholder="22"
                  :rules="[
                    v => v !== null && v !== undefined || 'Días de vacaciones es obligatorio',
                    v => v >= 0 || 'No puede ser negativo',
                    v => v <= 365 || 'No puede ser mayor a 365 días'
                  ]"
                  required
                  outlined
                  dense
                  hint="Número de días de vacaciones que tiene el usuario al año"
                  persistent-hint
                ></v-text-field>

                <work-schedule-table
                  ref="scheduleTable"
                  v-model="form_data.work_schedule.horario_semanal"
                ></work-schedule-table>
              </div>

              <v-text-field
                v-if="editing_user && (i_am_staff || i_am_loginas) && !is_owner"
                v-model="form_data.id"
                label="ID de Usuario"
                disabled
                outlined
                dense
              ></v-text-field>
            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="dialog_visible = false">
            Cancelar
          </v-btn>
          <v-btn color="blue darken-1" text @click="saveUser">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para cambiar contraseña -->
    <v-dialog v-model="change_password_dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Cambiar Contraseña</span>
        </v-card-title>

        <div class="subtitle-vicky">
          Este usuario pertenece a la empresa: {{ companyName }}
        </div>

        <v-card-text>
          <v-container>
            <v-form ref="changePasswordForm">
              <v-text-field
                v-model="password_data.password"
                counter
                label="Nueva contraseña"
                prepend-inner-icon="mdi-lock-outline"
                hint="Mínimo 8 caracteres"
                :rules="[
                  v => !!v || 'Contraseña es obligatoria',
                  v => v.length >= 8 || 'Mínimo 8 caracteres'
                ]"
                :type="show_password ? 'text' : 'password'"
                :append-icon="show_password ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="show_password = !show_password"
                required
                outlined
                dense
              ></v-text-field>

              <v-text-field
                v-model="password_data.password_repeat"
                counter
                label="Repetir nueva contraseña"
                prepend-inner-icon="mdi-lock-outline"
                hint="Mínimo 8 caracteres"
                :rules="[
                  v => !!v || 'Repetir contraseña es obligatorio',
                  v => v.length >= 8 || 'Mínimo 8 caracteres',
                  v => v === password_data.password || 'Las contraseñas no coinciden'
                ]"
                :type="show_password_repeat ? 'text' : 'password'"
                :append-icon="show_password_repeat ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="show_password_repeat = !show_password_repeat"
                required
                outlined
                dense
              ></v-text-field>
            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="cancelChangePassword">
            Cancelar
          </v-btn>
          <v-btn color="blue darken-1" text @click="saveChangePassword">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.visible"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      <b>{{ snackbar.text }}</b>
    </v-snackbar>

    <!-- Popup de motivos de ausencias -->
    <absence-reasons-popup
      :visible="absence_reasons_dialog"
      :companydb-id="companydb_id"
      @close="absence_reasons_dialog = false"
      @saved="onAbsenceReasonsSaved"
    />

    <!-- Popup de solicitudes -->
    <solicitudes-admin-popup
      :visible="solicitudes_dialog"
      :companydb-id="companydb_id"
      @close="solicitudes_dialog = false"
    />

    <!-- Popup de historial de empleados -->
    <employee-history-popup
      :visible="employee_history_dialog"
      :users="users"
      :companydb-id="companydb_id"
      @close="employee_history_dialog = false"
    />

    <v-dialog v-model="confirm_dialog.visible" max-width="400px">
      <v-card>
        <v-card-title class="confirm-popup-header">
          <span class="confirm-headline">{{ confirm_dialog.title }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="confirm_dialog.visible = false" class="confirm-close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="confirm-popup-content">
          {{ confirm_dialog.message }}
        </v-card-text>
        <v-card-actions class="confirm-popup-actions">
          <v-spacer></v-spacer>
          <button class="btn-confirm-cancel" @click="confirm_dialog.visible = false">
            Cerrar
          </button>
          <button
            :class="confirm_dialog.action === 'approve' ? 'btn-confirm-approve' : 'btn-confirm-reject'"
            @click="executeConfirmedAction"
          >
            Aceptar
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { APIUserRepo } from '../infra/APIUserRepo';
import { LoadUsersQuery } from '../app/LoadUsersQuery';
import { CreateUserCommand } from '../app/CreateUserCommand';
import { UpdateUserCommand } from '../app/UpdateUserCommand';
import { ToggleUserActiveCommand } from '../app/ToggleUserActiveCommand';
import { PermissionsService } from '../domain/PermissionsService';
import { APIProcessedRequestRepository } from '../infra/APIProcessedRequestRepository';
import { LoadProcessedRequestsHistoryQuery } from '../app/LoadProcessedRequestsHistoryQuery';
import AbsenceReasonsPopup from './AbsenceReasonsPopup.vue';
import SolicitudesAdminPopup from './SolicitudesAdminPopup.vue';
import EmployeeHistoryPopup from './EmployeeHistoryPopup.vue';
import WorkScheduleTable from './WorkScheduleTable.vue';
import UpdateClockPopup from './UpdateClockPopup.vue';
import { APIClockRepository } from './APIClockRepository';
import { Clock } from '@/modules/working_hours/domain/Clock';
import { formatClockType, get_day_secs, get_day_pause_secs, formatTime, buildProgressSegments } from '@/modules/working_hours/domain/Utils';
import * as XLSX from 'xlsx';

export default {
  name: 'AdminUserManagement',
  components: {
    AbsenceReasonsPopup,
    SolicitudesAdminPopup,
    EmployeeHistoryPopup,
    WorkScheduleTable,
    UpdateClockPopup
  },
  data() {
    return {
      ui_loading: false,
      dialog_visible: false,
      error_message: '',
      success_message: '',

      activeSection: 'buzon',
      sidebarItemsBase: [
        { id: 'buzon', label: 'Buzón de entrada' },
        { id: 'historial', label: 'Historial', svgIcon: require('@/assets/historial_icon.svg') },
        { id: 'pendientes', label: 'Pendientes'},
        { id: 'vacaciones', label: 'Vacaciones', svgIcon: require('@/assets/holidays_icon.svg') },
        { id: 'ausencias', label: 'Ausencias', svgIcon: require('@/assets/ausencias_icon.svg') },
        { id: 'error-fichaje', label: 'Error Fichaje', svgIcon: require('@/assets/error_fichaje_icon.svg') },
        { id: 'baja-laboral', label: 'Baja Laboral', svgIcon: require('@/assets/baja_icon.svg') }
      ],

      users: [],

      editing_user: null,
      form_data: {
        id: null,
        username: '',
        email: '',
        password: '',
        password_repeat: '',
        friendship_kind: 'empleado',
        view_working_hours: false,
        work_schedule: {
          id: null,
          nombre_jornada: 'Jornada Completa',
          vacation_days: 22,
          horario_semanal: {
            lunes: { hora_entrada: '09:00', hora_salida: '17:00' },
            martes: { hora_entrada: '09:00', hora_salida: '17:00' },
            miercoles: { hora_entrada: '09:00', hora_salida: '17:00' },
            jueves: { hora_entrada: '09:00', hora_salida: '17:00' },
            viernes: { hora_entrada: '09:00', hora_salida: '17:00' },
            sabado: { hora_entrada: null, hora_salida: null },
            domingo: { hora_entrada: null, hora_salida: null }
          }
        }
      },

      currentView: 'users',

      // Registro de Jornada
      rj_selectedPeriod: 'dia',
      rj_currentDate: new Date(),
      periodOptions: [
        { label: 'Día', value: 'dia' },
        { label: 'Semana', value: 'semana' },
        { label: 'Mes', value: 'mes' },
        { label: 'Año', value: 'ano' },
      ],
      rj_checkedUsers: [],
      rj_invertedOrder: false,
      rj_alphabeticOrder: false,
      rj_selectedEmployee: null,
      rj_clocks: [],
      rj_loading: false,
      rj_filterMenu: false,
      rj_filter: {
        startDate: '',
        endDate: ''
      },
      rj_clockRepo: null,
      rj_snackbar: {
        visible: false,
        text: '',
        color: 'error'
      },
      rj_editClockPopup: {
        show: false,
        clock: null,
        errors: {}
      },
      rj_dayClockDetail: {
        show: false,
        date: '',
        clocks: []
      },
      rj_editDayPopup: {
        show: false,
        loading: false,
        userId: null,
        userName: '',
        entrada: '',
        pausaInicio: '',
        pausaFin: '',
        salida: '',
        entradaMenu: false,
        pausaInicioMenu: false,
        pausaFinMenu: false,
        salidaMenu: false,
        // UUIDs originales para saber si borrar
        originalEntradaUuid: null,
        originalPausaInicioUuid: null,
        originalPausaFinUuid: null,
        originalSalidaUuid: null
      },
      rj_message: {
        quickMessage: null,
        body: ''
      },
      rj_setClockPopup: {
        show: false,
        formValid: false,
        loading: false,
        dateMenu: false,
        timeMenu: false,
        form: {
          date: '',
          time: '',
          type: 'in'
        }
      },

      // Progress bar data for employee list in Registro de Jornada
      rj_progressData: {},
      rj_progressLoading: false,
      rj_progressTimer: null,

      // Week view progress data (all employees, Mon-Sun aggregated)
      rj_weekProgressData: {},
      rj_weekProgressLoading: false,
      rj_weekProgressTimer: null,

      rj_monthProgressData: {},
      rj_monthProgressLoading: false,

      rj_yearProgressData: {},
      rj_yearProgressLoading: false,


      rj_weekSummary: {
        loading: false,
        days: [],
        totalWorkedMinutes: 0,
        totalExpectedMinutes: 0,
        schedule: null
      },
      rj_weekSummaryTimer: null,

      // Monthly hours summary for employee detail view
      rj_monthSummary: {
        loading: false,
        days: [],
        totalWorkedMinutes: 0,
        totalExpectedMinutes: 0,
        schedule: null
      },

      headers: [
        { text: 'Username', value: 'username', sortable: true },
        { text: 'Email', value: 'email', sortable: true },
        { text: 'Rol', value: 'friendship_kind_display', sortable: true },
        { text: 'Acciones', value: 'actions', sortable: false },
      ],

      approvalsHeaders: [
        { text: 'Empleado', value: 'empleado_display', sortable: true, width: '300px' },
        { text: 'Fecha', value: 'fecha', sortable: true, width: '90px', align: 'center' },
        { text: 'Acción/Original', value: 'accion_original', sortable: true, width: '120px', align: 'center' },
        { text: 'Propuesta', value: 'propuesta', sortable: true, width: '100px', align: 'center' },
        { text: 'Jornada Registrada', value: 'jornada_registrada', sortable: true, width: '130px', align: 'center' },
        { text: 'Comentario', value: 'comentario', sortable: true },
        { text: '', value: 'actions', sortable: false, width: '1px', align: 'center' },
      ],

      // rjEmployeeHeaders moved to computed

      // Datos de aprobaciones (de momento vacío)
      approvals: [],

      // Historial de solicitudes procesadas
      processedRequestsHistory: [],
      history_loading: false,
      processedRequestRepo: null,
      historySearchQuery: '',
      historySearchExpanded: false,
      historyFilterMenu: false,
      menuFechaDesde: false,
      menuFechaHasta: false,
      historyFilters: {
        tipo: null,
        fecha_desde: null,
        fecha_hasta: null
      },
      tiposCambioOptions: [
        { text: 'Modificación de fichaje', value: 'Modificación de fichaje' },
        { text: 'Baja Laboral', value: 'Baja Laboral' },
        { text: 'Ausencia', value: 'Ausencia' },
        { text: 'Vacaciones', value: 'Vacaciones' }
      ],

      // Headers para la tabla del historial
      historyHeaders: [
        { text: 'Empleado', value: 'empleado_display', sortable: true, width: '200px' },
        { text: 'Tipo', value: 'tipo_solicitud', sortable: true, width: '140px', align: 'center' },
        { text: 'Fecha Solicitud', value: 'fecha_solicitud', sortable: true, width: '110px', align: 'center' },
        { text: 'Detalle', value: 'detalle', sortable: true, width: '150px', align: 'center' },
        { text: 'Comentario', value: 'comentario', sortable: true, width: '150px' },
        { text: 'Estado', value: 'estado', sortable: true, width: '100px', align: 'center' },
        { text: 'Fecha Resolución', value: 'fecha_resolucion', sortable: true, width: '120px', align: 'center' },
      ],

      confirm_dialog: {
        visible: false,
        title: '',
        message: '',
        action: '', 
        item: null
      },

      friendshipKindOptions: [
        { text: 'Empleado', value: 'empleado' },
        { text: 'Administrador', value: 'admin' },
      ],

      // Repositorio y comandos
      userRepo: null,

      // Snackbar
      snackbar: {
        visible: false,
        text: '',
        color: 'error',
        timeout: 3000
      },

      // Datos para cambiar contraseña
      change_password_dialog: false,
      password_data: {
        user_id: null,
        password: '',
        password_repeat: '',
      },
      show_password: false,
      show_password_repeat: false,

      // Dialog de motivos de ausencias
      absence_reasons_dialog: false,
      solicitudes_dialog: false,
      employee_history_dialog: false,

      // Dropdown Mi perfil
      showProfileMenu: false,
    };
  },

  computed: {
    ...mapGetters({
      userData: 'me/userData',
      session_information: 'me/session_information',
      currentCompany: 'getCurrentCompany',
    }),
    rjEmployeeHeaders() {
      if (this.rj_selectedPeriod === 'semana') {
        return [
          { text: 'Empleado', value: 'username', width: '250px' },
          { text: 'Estado', value: 'estado' },
          { text: 'Lunes', value: 'lunes', align: 'center' },
          { text: 'Martes', value: 'martes', align: 'center' },
          { text: 'Miércoles', value: 'miercoles', align: 'center' },
          { text: 'Jueves', value: 'jueves', align: 'center' },
          { text: 'Viernes', value: 'viernes', align: 'center' },
          { text: 'Sábado', value: 'sabado', align: 'center' },
          { text: 'Domingo', value: 'domingo', align: 'center' },
          { text: 'Hora Total Semanal', value: 'totalSemanal', align: 'center', width: '90px' },
          { text: 'Estado Cierre', value: 'estadoCierre', align: 'center', width: '90px' },
          { text: 'Acciones', value: 'acciones', align: 'center', sortable: false },
        ];
      }
      return [
        { text: 'Empleado', value: 'username', width: '250px' },
        { text: 'Estado', value: 'estado' },
        { text: 'Entrada', value: 'entrada', align: 'center' },
        { text: 'Pausa', value: 'pausa', align: 'center' },
        { text: 'Salida', value: 'salida', align: 'center' },
        { text: 'Total', value: 'total', align: 'center' },
        { text: 'Balance', value: 'balance', align: 'center' },
        { text: 'Gráfico', value: 'grafico' },
        { text: 'Acciones', value: 'acciones', align: 'center', sortable: false },
      ];
    },
    rjClockDetailHeaders() {
      return [
        { text: '', value: 'dateDisplay', width: '220px' },
        { text: 'Entrada', value: 'entrada', align: 'center' },
        { text: 'Pausa', value: 'pausa', align: 'center' },
        { text: 'Salida', value: 'salida', align: 'center' },
        { text: 'Total (Actual)', value: 'total', align: 'center' },
        { text: 'Balance', value: 'balance', align: 'center' },
        { text: 'Gráfico', value: 'grafico', sortable: false },
        { text: 'Estado', value: 'estado', align: 'center' },
      ];
    },
    rjMessageDateLabel() {
      const d = this.rj_currentDate;
      const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      return `${dayNames[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
    },
    rjQuickMessages() {
      return [
        'Recuerda fichar a la entrada',
        'Recuerda fichar a la salida',
        'Revisa tus fichajes del día',
        'Tienes fichajes pendientes de corrección',
      ];
    },
    rjClockDailyHistory() {
      if (!this.rj_clocks || this.rj_clocks.length === 0) return [];

      const dayKeyMap = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
      const schedule = this.rj_weekSummary ? this.rj_weekSummary.schedule : null;
      const DEFAULT_EXPECTED_SECS = 8 * 60 * 60;

      // Group clocks by date string
      const groups = {};
      for (const clock of this.rj_clocks) {
        const t = new Date(clock.timestamp);
        const dateStr = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        if (!groups[dateStr]) groups[dateStr] = [];
        groups[dateStr].push({
          uuid: clock.uuid,
          timestamp: t,
          type: clock.type,
          changes: clock.changes || [],
          location: clock.location || 'office'
        });
      }

      // Sort dates descending
      const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a));
      const now = new Date();

      return sortedDates.map(dateStr => {
        const dayClocks = groups[dateStr].sort((a, b) => a.timestamp - b.timestamp);
        const dayDate = new Date(dateStr + 'T00:00:00');
        const dow = dayDate.getDay();
        const dayKey = dayKeyMap[dow];

        // Expected secs
        let expectedSecs = DEFAULT_EXPECTED_SECS;
        if (schedule && schedule.horario_semanal) {
          const horarioDia = schedule.horario_semanal[dayKey];
          if (horarioDia && horarioDia.hora_entrada && horarioDia.hora_salida) {
            const [eh, em] = horarioDia.hora_entrada.split(':').map(Number);
            const [sh, sm] = horarioDia.hora_salida.split(':').map(Number);
            const mins = (sh * 60 + sm) - (eh * 60 + em);
            if (mins > 0) expectedSecs = mins * 60;
          }
        }

        const workedSecs = get_day_secs(dayClocks);
        const pauseSecs = get_day_pause_secs(dayClocks);

        // Entrada / Pausa / Salida
        const firstIn = dayClocks.find(c => c.type === 'in');
        const firstPauseIdx = dayClocks.findIndex(c => c.type === 'pause');
        let horaPausa = null;
        let horaFinPausa = null;
        if (firstPauseIdx !== -1) {
          horaPausa = dayClocks[firstPauseIdx].timestamp;
          const continueAfter = dayClocks.slice(firstPauseIdx + 1).find(c => c.type === 'continue');
          if (continueAfter) horaFinPausa = continueAfter.timestamp;
        }
        const lastOut = [...dayClocks].reverse().find(c => c.type === 'out');

        const fmtHM = (d) => {
          if (!d) return '--:--';
          const dt = new Date(d);
          return dt.getHours().toString().padStart(2, '0') + ':' + dt.getMinutes().toString().padStart(2, '0');
        };

        const entrada = firstIn ? fmtHM(firstIn.timestamp) : '--:--';
        const pi = fmtHM(horaPausa);
        const pf = fmtHM(horaFinPausa);
        const pausaRango = `${pi} - ${pf}`;
        const salida = lastOut ? fmtHM(lastOut.timestamp) : '--:--';

        const totalH = Math.floor(workedSecs / 3600);
        const totalM = Math.floor((workedSecs % 3600) / 60);
        const total = `${totalH}h ${totalM}min`;

        // Balance
        const diffSecs = Math.round(workedSecs + pauseSecs - expectedSecs);
        const absDiff = Math.abs(diffSecs);
        const bH = Math.floor(absDiff / 3600);
        const bM = Math.floor((absDiff % 3600) / 60);
        const sign = diffSecs > 0 ? '+' : diffSecs < 0 ? '-' : '';
        const balanceFormatted = `${sign}${bH}h ${bM}min`;

        // Estado
        const pastClocks = dayClocks.filter(c => c.timestamp.getTime() <= now.getTime());
        const lastType = pastClocks.length > 0 ? pastClocks[pastClocks.length - 1].type : null;
        const isActive = (lastType === 'in' || lastType === 'continue');
        const isPaused = (lastType === 'pause');
        const isCompleted = expectedSecs > 0 && workedSecs >= expectedSecs;

        let estado;
        if (dayClocks.length === 0) {
          estado = { text: 'No iniciado', svgAsset: 'point_notstarted.svg' };
        } else if (isCompleted) {
          estado = { text: 'Completado', svgAsset: 'point_completed.svg' };
        } else if (isActive) {
          estado = { text: 'Activo', svgAsset: 'point_active.svg' };
        } else if (isPaused) {
          estado = { text: 'Pausa', svgAsset: 'point_pause.svg' };
        } else if (lastOut) {
          estado = { text: 'Incompleto', svgAsset: 'point_incomplete.svg' };
        } else {
          estado = { text: 'No iniciado', svgAsset: 'point_notstarted.svg' };
        }

        // Progress segments
        const segments = buildProgressSegments(dayClocks, expectedSecs);

        // Date display
        const dayNamesLong = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const parts = dateStr.split('-');
        const dayNameLong = dayNamesLong[dow];
        const dateFormatted = `${parts[2]}/${parts[1]}/${parts[0]}`;

        return {
          dateStr,
          dateDisplay: `${dayNameLong} ${dateFormatted}`,
          dayNameLong,
          dateFormatted,
          entrada,
          pausa: pausaRango,
          salida,
          total,
          balanceDiffSecs: diffSecs,
          balanceFormatted,
          estado,
          segments,
          isCompleted,
          clocks: dayClocks
        };
      });
    },
    rjMonthDays() {
      const d = this.rj_currentDate;
      const year = d.getFullYear();
      const month = d.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const dayLetters = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
      const days = [];
      for (let i = 1; i <= daysInMonth; i++) {
        const date = new Date(year, month, i);
        const dow = date.getDay();
        days.push({
          dayNumber: i,
          dayLetter: dayLetters[dow],
          isWeekend: dow === 0 || dow === 6,
          value: `mes_dia_${i}`
        });
      }
      return days;
    },
    rjMonthEmployeeHeaders() {
      const dayHeaders = this.rjMonthDays.map(d => ({
        text: String(d.dayNumber),
        value: d.value,
        align: 'center',
        sortable: false
      }));
      return [
        { text: 'Empleado', value: 'username', width: '200px' },
        ...dayHeaders,
        { text: 'Hora Total Mensual', value: 'totalMensual', align: 'center', sortable: false },
        { text: 'Vacaciones', value: 'vacaciones', align: 'center', sortable: false },
        { text: 'Ausencias', value: 'ausencias', align: 'center', sortable: false },
        { text: 'Estado Cierre Prev.', value: 'estadoCierrePrev', align: 'center', sortable: false },
        { text: 'Acciones', value: 'acciones', align: 'center', sortable: false },
      ];
    },
    rjYearEmployeeHeaders() {
      return [
        { text: 'Empleado', value: 'username', width: '250px' },
        { text: 'Contrato Anual', value: 'contratoAnual', align: 'center' },
        { text: 'Horas trabajadas', value: 'horasTrabajadas', align: 'center' },
        { text: 'Balance', value: 'balanceAnual', align: 'center' },
        { text: 'Acciones', value: 'acciones', align: 'center', sortable: false },
      ];
    },
    rjSortedUsers() {
      let result = [...this.users];
      if (this.rj_alphabeticOrder) {
        result.sort((a, b) => {
          const nameA = (a.displayName || a.username || '').toLowerCase();
          const nameB = (b.displayName || b.username || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }
      if (this.rj_invertedOrder) {
        result.reverse();
      }
      return result;
    },
    rj_activeProgressData() {
      if (this.rj_selectedPeriod === 'semana') return this.rj_weekProgressData;
      return this.rj_progressData;
    },
    rj_currentDateFull() {
      const d = this.rj_currentDate;
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

      if (this.rj_selectedPeriod === 'mes') {
        return `${months[d.getMonth()]} ${d.getFullYear()}`;
      }

      if (this.rj_selectedPeriod === 'semana') {
        const dayIdx = (d.getDay() + 6) % 7;
        const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dayIdx);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        const monthsShort = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
          'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        const monStr = `${monday.getDate()} ${monthsShort[monday.getMonth()]}`;
        const sunStr = `${sunday.getDate()} ${monthsShort[sunday.getMonth()]}`;
        return `Semana del ${monStr} - ${sunStr} ${sunday.getFullYear()}`;
      }

      const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      return `${days[d.getDay()]}, ${d.getDate()} de ${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    rj_currentDateLabel() {
      const d = this.rj_currentDate;
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
        'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

      if (this.rj_selectedPeriod === 'ano') {
        return `${d.getFullYear()}`;
      }

      if (this.rj_selectedPeriod === 'mes') {
        return `${months[d.getMonth()]} ${d.getFullYear()}`;
      }

      if (this.rj_selectedPeriod === 'semana') {
        const dayIdx = (d.getDay() + 6) % 7;
        const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dayIdx);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return `${monday.getDate()} ${months[monday.getMonth()]} - ${sunday.getDate()} ${months[sunday.getMonth()]}`;
      }

      return `${d.getDate()} ${months[d.getMonth()]}`;
    },
    is_admin() {
      return PermissionsService.canManageUsers(this.userData);
    },
    is_owner() {
      if (!this.userData) return false;
      // Revisar tanto friendship_kind como user_role
      const isFriendshipOwner = this.userData.friendship_kind === 'propietario';
      const isUserRoleOwner = this.userData.user_role && this.userData.user_role.value === 'owner';
      return isFriendshipOwner || isUserRoleOwner;
    },
    can_edit_absence_reasons() {
      // Permitir a admins y propietarios editar motivos de ausencias
      if (!this.userData) return false;
      const isOwner = this.userData.friendship_kind === 'propietario';
      const isAdmin = this.userData.friendship_kind === 'admin';
      return isOwner || isAdmin || this.is_admin;
    },
    i_am_staff() {
      return this.userData && this.userData.is_staff;
    },
    i_am_loginas() {
      return (
        this.userData &&
        this.userData.hasOwnProperty('username') &&
        this.session_information &&
        this.session_information.hasOwnProperty('username') &&
        this.session_information.username !== this.userData.username
      );
    },
    companyName() {
      return this.currentCompany && this.currentCompany.name ? this.currentCompany.name : 'la empresa actual';
    },
    companydb_id() {
      // Intentar obtener de múltiples fuentes
      if (this.$route.params.companydb_id) {
        return parseInt(this.$route.params.companydb_id);
      }
      if (this.userData && this.userData.company_id) {
        return parseInt(this.userData.company_id);
      }
      if (this.userData && this.userData.companies && this.userData.companies.length > 0) {
        return parseInt(this.userData.companies[0].id);
      }
      if (this.userData && this.userData.contracts && this.userData.contracts.length > 0) {
        // Buscar un contrato de working_hours activo
        const whContract = this.userData.contracts.find(c => c.kind === 'working_hours' && c.is_active);
        if (whContract && whContract.companydbs && whContract.companydbs.length > 0) {
          return parseInt(whContract.companydbs[0].id);
        }
      }
      return null;
    },
    sidebarItems() {
      const countByTipo = {};
      const countableSections = ['vacaciones', 'ausencias', 'error-fichaje', 'baja-laboral'];
      countableSections.forEach(s => { countByTipo[s] = 0; });
      this.approvals.forEach(a => {
        if (countByTipo[a.tipo] !== undefined) {
          countByTipo[a.tipo]++;
        }
      });
      const totalPending = this.approvals.length;
      return this.sidebarItemsBase.map(item => {
        if (item.id === 'buzon' || item.id === 'pendientes') {
          return { ...item, count: totalPending > 0 ? totalPending : undefined };
        }
        if (countableSections.includes(item.id)) {
          const c = countByTipo[item.id];
          return { ...item, count: c > 0 ? c : undefined };
        }
        return { ...item };
      });
    },
    filteredApprovals() {
      if (this.currentView !== 'approvals') {
        return [];
      }

      if (this.activeSection === 'buzon' || this.activeSection === 'pendientes') {
        return this.approvals;
      }

      if (['vacaciones', 'ausencias', 'error-fichaje', 'baja-laboral'].includes(this.activeSection)) {
        return this.approvals.filter(a => a.tipo === this.activeSection);
      }

      return [];
    },
    filteredHistoryItems() {
      let items = this.processedRequestsHistory;

      if (this.historySearchQuery && this.historySearchQuery.trim() !== '') {
        const query = this.historySearchQuery.toLowerCase().trim();
        items = items.filter(item => {
          return (
            (item.empleado_display && item.empleado_display.toLowerCase().includes(query)) ||
            (item.tipo_solicitud && item.tipo_solicitud.toLowerCase().includes(query)) ||
            (item.comentario && item.comentario.toLowerCase().includes(query)) ||
            (item.estado && item.estado.toLowerCase().includes(query)) ||
            (item.detalle && item.detalle.toLowerCase().includes(query))
          );
        });
      }

      // Filtro por tipo de cambio
      if (this.historyFilters.tipo) {
        items = items.filter(item => {
          return item.tipo_solicitud && item.tipo_solicitud.toLowerCase().includes(this.historyFilters.tipo.toLowerCase());
        });
      }

      // Filtro por fecha desde
      if (this.historyFilters.fecha_desde) {
        const fechaDesde = new Date(this.historyFilters.fecha_desde);
        items = items.filter(item => {
          if (!item.fecha_solicitud || item.fecha_solicitud === '-') return false;
          const itemDate = this.parseDate(item.fecha_solicitud);
          return itemDate >= fechaDesde;
        });
      }

      // Filtro por fecha hasta
      if (this.historyFilters.fecha_hasta) {
        const fechaHasta = new Date(this.historyFilters.fecha_hasta);
        fechaHasta.setHours(23, 59, 59, 999);
        items = items.filter(item => {
          if (!item.fecha_solicitud || item.fecha_solicitud === '-') return false;
          const itemDate = this.parseDate(item.fecha_solicitud);
          return itemDate <= fechaHasta;
        });
      }

      return items;
    },
    hasActiveHistoryFilters() {
      return (
        this.historyFilters.tipo ||
        this.historyFilters.fecha_desde ||
        this.historyFilters.fecha_hasta ||
        this.historySearchQuery
      );
    },
    formatFilterDateRange() {
      const desde = this.historyFilters.fecha_desde;
      const hasta = this.historyFilters.fecha_hasta;
      if (desde && hasta) {
        return `${this.formatDateShort(desde)} - ${this.formatDateShort(hasta)}`;
      } else if (desde) {
        return `Desde ${this.formatDateShort(desde)}`;
      } else if (hasta) {
        return `Hasta ${this.formatDateShort(hasta)}`;
      }
      return '';
    }
  },

  watch: {
    '$route.params.companydb_id': {
      async handler(newVal, oldVal) {
        if (newVal !== oldVal && newVal) {
          await this.loadUsers(true);
        }
      }
    },
    currentView(newVal) {
      if (newVal !== 'registro-jornada') {
        this.rjStopProgressTimer();
        this.rjStopWeekProgressTimer();
        this.rj_progressData = {};
        this.rj_weekProgressData = {};
      }
    },
    rj_currentDate() {
      if (this.currentView === 'registro-jornada') {
        if (this.rj_selectedPeriod === 'semana') {
          this.rjLoadWeekProgressData();
        } else if (this.rj_selectedPeriod === 'mes') {
          this.rjLoadMonthProgressData();
        } else if (this.rj_selectedPeriod === 'ano') {
          this.rjLoadYearProgressData();
        } else {
          this.rjLoadProgressData();
        }
      }
    },

    rj_selectedPeriod(newVal) {
      if (this.currentView !== 'registro-jornada') return;
      this.rj_checkedUsers = [];
      this.rj_selectedEmployee = null;
      if (newVal === 'semana') {
        this.rjStopProgressTimer();
        this.rjLoadWeekProgressData();
      } else if (newVal === 'dia') {
        this.rjStopWeekProgressTimer();
        this.rjLoadProgressData();
      } else if (newVal === 'mes') {
        this.rjStopWeekProgressTimer();
        this.rjStopProgressTimer();
        this.rjLoadMonthProgressData();
      } else if (newVal === 'ano') {
        this.rjStopWeekProgressTimer();
        this.rjStopProgressTimer();
        this.rjLoadYearProgressData();
      }
    }
  },

  beforeDestroy() {
    this.rjStopProgressTimer();
    this.rjStopWeekSummaryTimer();
    this.rjStopWeekProgressTimer();
  },

  created() {
    this.userRepo = new APIUserRepo(this.$store);
    this.processedRequestRepo = new APIProcessedRequestRepository();
  },

  async mounted() {

    if (this.is_admin) {
      if (!this.companydb_id) {
        await this.fetchCompanyId();
      }

      await this.loadUsers();
    } else {
       this.navigateTo('main');
    }
  },

  methods: {
    rjFormatHHMM(date) {
      if (!date) return '--:--';
      const d = new Date(date);
      return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
    },
    rjGetUserEstado(userId) {
      const data = this.rj_activeProgressData[userId];
      if (!data || !data.clocks || data.clocks.length === 0) {
        return { text: 'No iniciado', color: 'transparent', icon: null, dark: false, useSvg: true, svgAsset: 'point_notstarted.svg' };
      }
      const isCompleted = data.expectedSecs > 0 && data.workedSecs >= data.expectedSecs;
      if (isCompleted) {
        return { text: 'Completado', color: 'transparent', icon: null, dark: false, useSvg: true, svgAsset: 'point_completed.svg' };
      }
      if (data.isActive) {
        return { text: 'Activo', color: 'transparent', icon: null, dark: false, useSvg: true, svgAsset: 'point_active.svg' };
      }
      if (data.isPaused) {
        return { text: 'Pausa', color: 'transparent', icon: null, dark: false, useSvg: true, svgAsset: 'point_pause.svg' };
      }
      if (data.horaSalida) {
        return { text: 'Incompleto', color: 'transparent', icon: null, dark: false, useSvg: true, svgAsset: 'point_incomplete.svg' };
      }
      return { text: 'No iniciado', color: 'transparent', icon: null, dark: false, useSvg: true, svgAsset: 'point_notstarted.svg' };
    },
    rjGetUserDayInfo(userId) {
      const data = this.rj_activeProgressData[userId];
      if (!data) {
        return { entrada: '--:--', pausaInicio: '--:--', pausaFin: '--:--', pausaRango: '--:--', salida: '--:--', total: '00:00' };
      }
      const totalH = Math.floor(data.workedSecs / 3600);
      const totalM = Math.floor((data.workedSecs % 3600) / 60);
      const pi = this.rjFormatHHMM(data.horaPausa);
      const pf = this.rjFormatHHMM(data.horaFinPausa);
      return {
        entrada: this.rjFormatHHMM(data.horaEntrada),
        pausaInicio: pi,
        pausaFin: pf,
        pausaRango: `${pi} - ${pf}`,
        salida: this.rjFormatHHMM(data.horaSalida),
        total: `${totalH}h ${totalM}min`
      };
    },
    rjGetUserWeekDayHours(userId, dayIndex) {
      const data = this.rj_weekProgressData[userId];
      if (!data || !data.dayWorkedSecs || !data.dayWorkedSecs[dayIndex]) {
        return '00:00';
      }
      const secs = data.dayWorkedSecs[dayIndex];
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      return `${h}h ${m}min`;
    },
    rjGetUserWeekTotalHours(userId) {
      const data = this.rj_weekProgressData[userId];
      if (!data) {
        return '00:00';
      }
      const secs = data.workedSecs || 0;
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      return `${h}h ${m}min`;
    },
    rjGetUserWeekClosureStatus(userId) {
      const data = this.rj_weekProgressData[userId];
      if (!data) {
        return { text: 'Sin datos', bg: '#F2EBFC', icon: null };
      }
      const expectedSecs = data.expectedSecs || 0;
      const workedSecs = data.workedSecs || 0;
      if (expectedSecs === 0) {
        return { text: 'Sin horario', bg: '#F2EBFC', icon: null };
      }
      if (workedSecs >= expectedSecs) {
        return { text: 'Correcto', bg: '#D7FFDA', icon: require('@/assets/tic_icon.svg') };
      }
      // TEST: forzar incompleto
      // if (data._forceIncompleto) {
      //   return { text: 'Incompleto', bg: '#FFE7E7', icon: require('@/assets/warning_icon.svg') };
      // }
      // Check if the week is in the past
      const d = this.rj_currentDate;
      const dayIdx = (d.getDay() + 6) % 7;
      const sunday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dayIdx + 6, 23, 59, 59);
      const now = new Date();
      if (now > sunday) {
        return { text: 'Incompleto', bg: '#FFE7E7', icon: require('@/assets/warning_icon.svg') };
      }
      return { text: 'En proceso', bg: '#F2EBFC', icon: null };
    },
    rjToggleCheckAll() {
      if (this.rj_checkedUsers.length === this.users.length) {
        this.rj_checkedUsers = [];
      } else {
        this.rj_checkedUsers = this.users.map(u => u.id);
      }
    },
    rjToggleCheckUser(userId) {
      const idx = this.rj_checkedUsers.indexOf(userId);
      if (idx === -1) {
        this.rj_checkedUsers.push(userId);
      } else {
        this.rj_checkedUsers.splice(idx, 1);
      }
    },
    rjDownloadExcel() {
      if (this.rj_checkedUsers.length === 0) return;

      const selectedUsers = this.rjSortedUsers.filter(u => this.rj_checkedUsers.includes(u.id));

      const rows = [];
      // Header row (merged groups)
      rows.push(['Empleado', 'Estado', 'Balance', '', '', '', '', '']);
      // Subheader row
      rows.push(['', '', 'Entrada', 'Pausa', 'Salida', 'Total', 'Balance', 'Gráfico']);

      for (const user of selectedUsers) {
        const dayInfo = this.rjGetUserDayInfo(user.id);
        const estado = this.rjGetUserEstado(user.id);
        const balance = this.rjGetUserBalance(user.id);
        const progress = this.rjGetUserProgress(user.id);
        rows.push([
          user.displayName || user.username,
          estado.text,
          dayInfo.entrada,
          dayInfo.pausaRango,
          dayInfo.salida,
          dayInfo.total,
          balance.formatted,
          `${Math.round(progress.percentage)}%`
        ]);
      }

      const ws = XLSX.utils.aoa_to_sheet(rows);

      // Merge cells for header row to match table layout
      ws['!merges'] = [
        { s: { r: 0, c: 2 }, e: { r: 0, c: 7 } }, // "Balance" spans columns C-H
      ];

      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Empleado
        { wch: 14 }, // Estado
        { wch: 12 }, // Entrada
        { wch: 18 }, // Pausa
        { wch: 12 }, // Salida
        { wch: 12 }, // Total
        { wch: 14 }, // Balance
        { wch: 10 }, // Gráfico
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Registro Jornada');

      const d = this.rj_currentDate;
      let dateStr;
      if (this.rj_selectedPeriod === 'semana') {
        const dayIdx = (d.getDay() + 6) % 7;
        const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dayIdx);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        const monStr = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
        const sunStr = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`;
        dateStr = `${monStr}_${sunStr}`;
      } else {
        dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
      XLSX.writeFile(wb, `registro_jornada_${dateStr}.xlsx`);
    },
    rjPrevDate() {
      const d = new Date(this.rj_currentDate);
      if (this.rj_selectedPeriod === 'ano') {
        d.setFullYear(d.getFullYear() - 1);
      } else if (this.rj_selectedPeriod === 'mes') {
        d.setMonth(d.getMonth() - 1);
      } else {
        const step = this.rj_selectedPeriod === 'semana' ? 7 : 1;
        d.setDate(d.getDate() - step);
      }
      this.rj_currentDate = d;
    },
    rjNextDate() {
      const d = new Date(this.rj_currentDate);
      if (this.rj_selectedPeriod === 'ano') {
        d.setFullYear(d.getFullYear() + 1);
      } else if (this.rj_selectedPeriod === 'mes') {
        d.setMonth(d.getMonth() + 1);
      } else {
        const step = this.rj_selectedPeriod === 'semana' ? 7 : 1;
        d.setDate(d.getDate() + step);
      }
      this.rj_currentDate = d;
    },
    getRowClass(item) {
      return item.is_active ? '' : 'inactive-user-row';
    },
    showSnackbar(color, text) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },
    getTitleText() {
      // Para owners: solo "Editar Horario de Trabajo"
      if (this.is_owner) {
        return 'Editar Horario de Trabajo';
      }
      // Para no-owners (admins): título completo
      return this.editing_user ? 'Editar Usuario' : 'Crear Nuevo Usuario';
    },
    getCompanyId() {
      const companyId = this.companydb_id || this.$route.params.companydb_id || (this.$store.getters["me/userData"] && this.$store.getters["me/userData"].company_id) || "1";
      return companyId;
    },
    navigateTo(section) {
      const companyId = this.getCompanyId();
      const targetRoute = `/${companyId}/working-hours${section === 'main' ? '' : '-' + section.replace('-', '-')}`;
      const routes = {
        "main": `/${companyId}/working-hours`,
        "absences": `/${companyId}/working-hours-absences`,
        "holidays": `/${companyId}/working-hours-holidays`,
        "history": `/${companyId}/working-hours-history`,
        "admin-month": `/${companyId}/working-hours-admin-month`,
        "admin-users": `/${companyId}/working-hours-admin-users`
      };
      var result = this.$router.push(routes[section]);
      if (result && result.catch) {
        result.catch(function() {});
      }
    },

    async loadUsers(force_reload = false) {
      if (this.ui_loading && !force_reload) return;

      const screenData = {
        users: [],
        ui_loading: false
      };

      const query = new LoadUsersQuery(this.userRepo, screenData);

      try {
        await query.execute();

        // Mapear usuarios con datos de visualización
        let mappedUsers = screenData.users.map(user => ({
          ...user,
          is_owner: user.friendship_kind === 'propietario',
          friendship_kind_display: this.getFriendshipKindDisplay(user.friendship_kind),
          displayName: this.buildEmpleadoDisplay(user),
        }));

        // Asegurar que el usuario autenticado esté primero
        const currentUserId = this.userData && this.userData.id;
        if (currentUserId) {
          // Separar el usuario autenticado del resto
          const currentUser = mappedUsers.find(u => u.id === currentUserId);
          const otherUsers = mappedUsers.filter(u => u.id !== currentUserId);

          // Poner el usuario autenticado primero
          if (currentUser) {
            mappedUsers = [currentUser, ...otherUsers];
          }
        }

        this.users = mappedUsers;
        this.ui_loading = screenData.ui_loading;
      } catch (error) {
        this.showSnackbar('error', 'Error al cargar usuarios');
        this.ui_loading = false;
      }
    },

    getFriendshipKindDisplay(kind) {
      const kindMap = {
        'propietario': 'Propietario',
        'admin': 'Administrador',
        'empleado': 'Empleado',
        'cliente': 'Cliente',
      };
      return kindMap[kind] || kind;
    },

    async openUserDialog(user) {
      if (user) {
        this.editing_user = user;

        // SIEMPRE crear un objeto nuevo con valores por defecto limpios
        const defaultWorkSchedule = {
          id: null,
          nombre_jornada: 'Jornada Completa',
          vacation_days: 22,
          horario_semanal: {
            lunes: { hora_entrada: '09:00', hora_salida: '17:00' },
            martes: { hora_entrada: '09:00', hora_salida: '17:00' },
            miercoles: { hora_entrada: '09:00', hora_salida: '17:00' },
            jueves: { hora_entrada: '09:00', hora_salida: '17:00' },
            viernes: { hora_entrada: '09:00', hora_salida: '17:00' },
            sabado: { hora_entrada: null, hora_salida: null },
            domingo: { hora_entrada: null, hora_salida: null }
          }
        };

        this.form_data = {
          id: user.id,
          username: user.username,
          email: user.email,
          password: '', // Vacío por defecto, solo se cambia si el usuario lo rellena
          password_repeat: '',
          friendship_kind: user.friendship_kind,
          view_working_hours: true,
          work_schedule: JSON.parse(JSON.stringify(defaultWorkSchedule)) // Clonar profundo
        };

        // Cargar el work_schedule existente del usuario
        try {
          const { query_dispatcher } = await import('@/api/client');
          const schedule = await query_dispatcher.dispatch('get_work_schedule_by_user.query', {
            user_id: user.id
          });

          if (schedule && schedule.id && schedule.user_id === user.id) {
            this.form_data.view_working_hours = true;
            this.form_data.work_schedule = {
              id: schedule.id,
              nombre_jornada: schedule.nombre_jornada,
              vacation_days: schedule.vacation_days || 22,
              horario_semanal: JSON.parse(JSON.stringify(schedule.horario_semanal)) // Clonar profundo
            };
          } else {
            this.form_data.view_working_hours = false;
            this.form_data.work_schedule = {
              id: null,
              nombre_jornada: 'Jornada Completa',
              vacation_days: 22,
              horario_semanal: {
                lunes: { hora_entrada: '09:00', hora_salida: '17:00' },
                martes: { hora_entrada: '09:00', hora_salida: '17:00' },
                miercoles: { hora_entrada: '09:00', hora_salida: '17:00' },
                jueves: { hora_entrada: '09:00', hora_salida: '17:00' },
                viernes: { hora_entrada: '09:00', hora_salida: '17:00' },
                sabado: { hora_entrada: null, hora_salida: null },
                domingo: { hora_entrada: null, hora_salida: null }
              }
            };
          }
        } catch (error) {
          console.error('Error loading work schedule:', error);
        }
      } else {
        this.editing_user = null;
        this.form_data = {
          id: null,
          username: '',
          email: '',
          password: '',
          password_repeat: '',
          friendship_kind: 'empleado',
          view_working_hours: false,
          work_schedule: {
            id: null,
            nombre_jornada: 'Jornada Completa',
            vacation_days: 22,
            horario_semanal: {
              lunes: { hora_entrada: '09:00', hora_salida: '17:00' },
              martes: { hora_entrada: '09:00', hora_salida: '17:00' },
              miercoles: { hora_entrada: '09:00', hora_salida: '17:00' },
              jueves: { hora_entrada: '09:00', hora_salida: '17:00' },
              viernes: { hora_entrada: '09:00', hora_salida: '17:00' },
              sabado: { hora_entrada: null, hora_salida: null },
              domingo: { hora_entrada: null, hora_salida: null }
            }
          }
        };
        if (this.$refs.userForm) {
          this.$refs.userForm.resetValidation();
        }
      }
      this.dialog_visible = true;
    },

    async saveUser() {
      if (!this.$refs.userForm.validate()) {
        return;
      }

      // Validar horario si tiene acceso a fichajes
      if (this.form_data.view_working_hours) {
        if (this.$refs.scheduleTable && !this.$refs.scheduleTable.validate()) {
          this.showSnackbar('error', 'Por favor complete correctamente el horario de trabajo');
          return;
        }
      }

      // Validar contraseñas solo al crear usuario
      if (!this.editing_user) {
        // Al crear usuario, la contraseña es obligatoria
        if (!this.form_data.password) {
          this.showSnackbar('error', 'La contraseña es obligatoria para crear un usuario');
          return;
        }
        if (this.form_data.password !== this.form_data.password_repeat) {
          this.showSnackbar('error', 'Las contraseñas no coinciden');
          return;
        }
        if (this.form_data.password.length < 8) {
          this.showSnackbar('error', 'La contraseña debe tener al menos 8 caracteres');
          return;
        }
      } else {
        // Al editar, solo validar si se proporcionó una contraseña
        if (this.form_data.password || this.form_data.password_repeat) {
          if (this.form_data.password !== this.form_data.password_repeat) {
            this.showSnackbar('error', 'Las contraseñas no coinciden');
            return;
          }
          if (this.form_data.password && this.form_data.password.length < 8) {
            this.showSnackbar('error', 'La contraseña debe tener al menos 8 caracteres');
            return;
          }
        }
      }

      const screenData = {
        ui_loading: false,
        error_message: '',
        success_message: ''
      };

      try {
        let success = false;
        let createdUserId = null;

        const companydb_id = this.companydb_id;

        if (!companydb_id) {
          this.ui_loading = false;
          this.error_message = 'No se pudo determinar la empresa. Por favor contacte con soporte.';
          alert(this.error_message);
          return;
        }

        if (this.editing_user) {
          // Actualizar usuario existente
          const command = new UpdateUserCommand(this.userRepo, screenData);
          const updateData = {
            email: this.form_data.email,
            friendship_kind: this.form_data.friendship_kind,
            companydb_id: companydb_id,
            view_working_hours: this.form_data.view_working_hours,
          };

          // Incluir contraseña solo si se proporcionó
          if (this.form_data.password && this.form_data.password.trim() !== '') {
            updateData.password = this.form_data.password;
          }

          success = await command.execute(this.form_data.id, updateData);

          // Si la actualización fue exitosa, actualizar/crear work_schedule solo si tiene acceso a fichajes
          if (success && this.form_data.view_working_hours) {
            try {
              const { sync_command_dispatcher } = await import('@/api/client');

              if (this.form_data.work_schedule.id) {
                await sync_command_dispatcher.dispatch('update_work_schedule.command', {
                  schedule_id: this.form_data.work_schedule.id,
                  nombre_jornada: this.form_data.work_schedule.nombre_jornada,
                  vacation_days: this.form_data.work_schedule.vacation_days,
                  horario_semanal: this.form_data.work_schedule.horario_semanal
                });
              } else {
                await sync_command_dispatcher.dispatch('create_work_schedule.command', {
                  user_id: this.form_data.id,
                  nombre_jornada: this.form_data.work_schedule.nombre_jornada,
                  vacation_days: this.form_data.work_schedule.vacation_days,
                  horario_semanal: this.form_data.work_schedule.horario_semanal
                });
              }
            } catch (scheduleError) {
              console.error('Error updating work schedule:', scheduleError);
              this.showSnackbar('warning', 'Usuario actualizado pero hubo un error al actualizar el horario');
            }
          }
        } else {
          // Crear nuevo usuario
          const command = new CreateUserCommand(this.userRepo, screenData);
          const result = await command.execute({
            username: this.form_data.username,
            email: this.form_data.email,
            password: this.form_data.password,
            friendship_kind: this.form_data.friendship_kind,
            companydb_id: companydb_id,
            view_working_hours: this.form_data.view_working_hours,
          });

          success = result.success;
          createdUserId = result.user_id;
        }

        this.ui_loading = screenData.ui_loading;

        if (success) {
          // Si se creó un usuario nuevo, actualizar su work_schedule (ya creado por la señal post_save)
          if (createdUserId && this.form_data.view_working_hours) {
            try {
              const { sync_command_dispatcher } = await import('@/api/client');

              await sync_command_dispatcher.dispatch('update_work_schedule_by_user.command', {
                user_id: createdUserId,
                nombre_jornada: this.form_data.work_schedule.nombre_jornada,
                vacation_days: this.form_data.work_schedule.vacation_days,
                horario_semanal: this.form_data.work_schedule.horario_semanal
              });

              this.showSnackbar('success', 'Usuario y horario creados correctamente');
            } catch (scheduleError) {
              console.error('Error creating work schedule:', scheduleError);
              this.showSnackbar('warning', 'Usuario creado pero hubo un error al crear el horario');
            }
          } else if (this.editing_user) {
            // Ya se mostró el mensaje en el bloque de edición o se mostrará el del screenData
            this.showSnackbar('success', 'Usuario y horario actualizados correctamente');
          } else {
            this.showSnackbar('success', screenData.success_message);
          }

          this.dialog_visible = false;
          // Recargar la lista de usuarios después de crear/actualizar
          await this.loadUsers(true);
        } else {
          this.showSnackbar('error', `Error: ${screenData.error_message}`);
        }
      } catch (error) {
        this.showSnackbar('error', `Error al guardar el usuario: ${error.message || error}`);
      } finally {
        this.editing_user = null;
      }
    },

    async toggleUserActive(user) {
      const screenData = {
        ui_loading: false,
        error_message: '',
        success_message: ''
      };

      const command = new ToggleUserActiveCommand(this.userRepo, screenData);

      try {
        // Invertir el estado
        const newState = !user.is_active;
        const companydb_id = this.companydb_id;
        const success = await command.execute(user.id, newState, companydb_id);

        if (success) {
          this.showSnackbar('success', screenData.success_message);
          await this.loadUsers(true);
        } else {
          this.showSnackbar('error', `Error: ${screenData.error_message}`);
        }
      } catch (error) {
        this.showSnackbar('error', 'Error al cambiar el estado del usuario');
      }
    },

    goToMonthPreview(user) {
      // Navegar a la previsualización del mes con el usuario seleccionado
      const companyId = this.getCompanyId();
      this.$router.push({
        path: `/${companyId}/working-hours-admin-month`,
        query: { userId: user.id }
      }).catch(err => {});
    },

    openWorkScheduleDialog(user) {
      // Abrir el diálogo de edición (para owners solo mostrará el horario)
      this.openUserDialog(user);
    },

    showAdminAccessInfo() {
      // Navegar a la pantalla de gestión completa de usuarios
      var result = this.$router.push('/users-permissions');
      if (result && result.catch) {
        result.catch(function() {});
      }
    },

    openChangePasswordDialog(user) {
      this.password_data.user_id = user.id;
      this.password_data.password = '';
      this.password_data.password_repeat = '';
      this.show_password = false;
      this.show_password_repeat = false;
      if (this.$refs.changePasswordForm) {
        this.$refs.changePasswordForm.resetValidation();
      }
      this.change_password_dialog = true;
    },

    cancelChangePassword() {
      this.change_password_dialog = false;
      this.password_data.user_id = null;
      this.password_data.password = '';
      this.password_data.password_repeat = '';
      this.show_password = false;
      this.show_password_repeat = false;
    },

    saveChangePassword() {
      if (!this.$refs.changePasswordForm.validate()) {
        return;
      }

      if (this.password_data.password !== this.password_data.password_repeat) {
        this.showSnackbar('error', 'Las contraseñas no coinciden');
        return;
      }

      // Usar el action de Vuex igual que en UsersPermissions.vue
      this.$store.dispatch('connector/changePassCDBUser', {
        user_id: this.password_data.user_id,
        pass: this.password_data.password
      }).then(response => {
        this.change_password_dialog = false;
        this.cancelChangePassword();
        this.showSnackbar('success', 'Contraseña actualizada con éxito');
      }).catch(error => {
        console.error(error);
        this.showSnackbar('error', 'Ha habido un problema al cambiar la contraseña');
      });
    },

    openAbsenceReasonsDialog() {
      this.absence_reasons_dialog = true;
    },

    async openSolicitudesDialog() {
      this.currentView = 'approvals';
      await this.loadPendingApprovals();
    },

    async loadPendingApprovals() {
      this.ui_loading = true;
      try {
        const { query_dispatcher } = await import('@/api/client');
        const mappedApprovals = [];

        try {
          const clockResult = await query_dispatcher.dispatch('get_pending_clock_modifications.query', {});
          if (clockResult && Array.isArray(clockResult)) {
            clockResult.forEach(clock => {
              if (clock.pending_changes && clock.pending_changes.length > 0) {
                clock.pending_changes.forEach(change => {
                  mappedApprovals.push({
                    tipo: 'error-fichaje',
                    solicitud_tipo: 'clock',
                    clock_uuid: clock.uuid,
                    change_index: change.index,
                    is_new_clock_request: change.is_new_clock_request || false,
                    username: clock.user_info ? clock.user_info.username : 'Desconocido',
                    empleado_display: this.buildEmpleadoDisplay(clock.user_info),
                    fecha: this.formatDate(clock.timestamp),
                    accion_original: change.index === 0
                      ? (change.justification && change.justification.toLowerCase().includes('olvidado') ? 'Olvido de fichaje' : 'Creación de fichaje')
                      : this.formatActionType(change.type) + ' / ' + this.formatTime(clock.timestamp),
                    propuesta: this.formatTime(change.timestamp),
                    jornada_registrada: change.index === 0 ? '' : this.formatDate(clock.timestamp),
                    comentario: change.justification || '-'
                  });
                });
              }
            });
          }
        } catch (e) {
          console.error('Error loading clock modifications:', e);
        }

        try {
          const absenceResult = await query_dispatcher.dispatch('filter_absences.query', {
            filters: [['status', '==', 'pending']]
          });
          if (absenceResult && Array.isArray(absenceResult)) {
            absenceResult.forEach(absence => {
              const tipoAusencia = absence.tipoAusencia || absence.tipo_ausencia || '';
              let tipo = 'ausencias';

              if (tipoAusencia === 'Vacaciones') {
                tipo = 'vacaciones';
              } else if (tipoAusencia && tipoAusencia.startsWith('Baja:')) {
                tipo = 'baja-laboral';
              }

              mappedApprovals.push({
                tipo: tipo,
                solicitud_tipo: 'absence',
                absence_uuid: absence.uuid,
                username: absence.user_info ? absence.user_info.username : (absence.username || 'Desconocido'),
                empleado_display: this.buildEmpleadoDisplay(absence.user_info, absence.username),
                fecha: this.formatDate(absence.start),
                accion_original: tipoAusencia || 'Ausencia',
                propuesta: this.formatDateRange(absence.start, absence.end),
                jornada_registrada: this.formatDate(absence.start),
                comentario: absence.reason || '-'
              });
            });
          }
        } catch (e) {
          console.error('Error loading absences:', e);
        }

        this.approvals = mappedApprovals;
      } catch (error) {
        console.error('Error loading pending approvals:', error);
        this.showSnackbar('error', 'Error al cargar las solicitudes pendientes');
        this.approvals = [];
      } finally {
        this.ui_loading = false;
      }
    },

    formatDateRange(start, end) {
      if (!start) return '-';
      const startDate = this.formatDate(start);
      const endDate = end ? this.formatDate(end) : startDate;
      if (startDate === endDate) {
        return startDate;
      }
      return `${startDate} - ${endDate}`;
    },

    buildEmpleadoDisplay(userInfo, fallbackUsername = null) {
      // Si no hay user_info, usar el fallback o 'Desconocido'
      if (!userInfo) {
        return fallbackUsername || 'Desconocido';
      }

      // Si existe first_name, construir el nombre completo
      if (userInfo.first_name) {
        let fullName = userInfo.first_name;
        if (userInfo.last_name) {
          fullName += ' ' + userInfo.last_name;
        }
        return fullName;
      }

      // Si no hay first_name, usar username
      return userInfo.username || fallbackUsername || 'Desconocido';
    },

    formatActionType(type) {
      const typeMap = {
        'in': 'Entrada',
        'out': 'Salida',
        'pause': 'Pausa',
        'continue': 'Continuación'
      };
      return typeMap[type] || type;
    },

    formatDate(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },

    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    },

    async loadProcessedRequestsHistory() {
      this.history_loading = true;
      try {
        const screenData = {
          history: [],
          ui_loading: false,
          error_message: undefined
        };

        const query = new LoadProcessedRequestsHistoryQuery(
          this.processedRequestRepo,
          screenData
        );

        await query.execute(100, 0);

        // Mapear los datos al formato de la tabla
        this.processedRequestsHistory = screenData.history.map(request => ({
          uuid: request.uuid,
          empleado_display: request.getEmployeeDisplayName(),
          tipo_solicitud: request.getRequestTypeLabel(),
          fecha_solicitud: this.formatDate(request.start || request.original_timestamp),
          detalle: this.formatRequestDetail(request),
          comentario: request.reason || request.justification || '-',
          estado: request.getStatusLabel(),
          fecha_resolucion: this.formatDate(request.getResolutionDate()),
          raw: request
        }));

        if (screenData.error_message) {
          this.showSnackbar('error', screenData.error_message);
        }
      } catch (error) {
        console.error('Error loading processed requests history:', error);
        this.showSnackbar('error', 'Error al cargar el historial de solicitudes');
        this.processedRequestsHistory = [];
      } finally {
        this.history_loading = false;
      }
    },

    formatRequestDetail(request) {
      if (request.request_type === 'absence') {
        return this.formatDateRange(request.start, request.end);
      } else {
        const originalTime = this.formatTime(request.original_timestamp);
        const proposedTime = this.formatTime(request.proposed_timestamp);
        const type = this.formatActionType(request.clock_type);
        return `${type}: ${originalTime} → ${proposedTime}`;
      }
    },

    toggleHistorySearch() {
      this.historySearchExpanded = !this.historySearchExpanded;
      if (this.historySearchExpanded) {
        this.$nextTick(() => {
          if (this.$refs.historySearchInput) {
            this.$refs.historySearchInput.focus();
          }
        });
      }
    },

    closeHistorySearch() {
      if (!this.historySearchQuery) {
        this.historySearchExpanded = false;
      }
    },

    onSearchBlur() {
      setTimeout(() => {
        if (!this.historySearchQuery) {
          this.historySearchExpanded = false;
        }
      }, 200);
    },

    applyHistoryFilter(filterType, value) {
      this.historyFilters[filterType] = value;
      this.historyFilterMenu = false;
    },

    removeHistoryFilter(filterType) {
      if (filterType === 'fecha') {
        this.historyFilters.fecha_desde = null;
        this.historyFilters.fecha_hasta = null;
      } else {
        this.historyFilters[filterType] = null;
      }
    },

    clearHistoryFilters() {
      this.historySearchQuery = '';
      this.historySearchExpanded = false;
      this.historyFilters = {
        tipo: null,
        fecha_desde: null,
        fecha_hasta: null
      };
    },

    parseDate(dateStr) {
      // Parsea fecha en formato dd/mm/yyyy
      if (!dateStr || dateStr === '-') return null;
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]);
      }
      return new Date(dateStr);
    },

    formatDateShort(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    },

    goBackToUsers() {
      this.currentView = 'users';
    },

    async toggleSidebarFilter(sectionId) {
      if (this.activeSection === sectionId) {
        this.activeSection = 'buzon';
      } else {
        this.activeSection = sectionId;
        if (sectionId === 'historial') {
          await this.loadProcessedRequestsHistory();
        }
      }
    },

    approveChange(item) {
      const isNewClock = item.solicitud_tipo === 'clock' && item.change_index === 0;
      this.confirm_dialog = {
        visible: true,
        title: isNewClock ? 'Confirmar creación de fichaje' : 'Confirmar aprobación',
        message: isNewClock
          ? `¿Estás seguro de que deseas crear este fichaje para ${item.username}?`
          : `¿Estás seguro de que deseas aprobar esta solicitud de ${item.username}?`,
        action: 'approve',
        item: item
      };
    },

    rejectChange(item) {
      this.confirm_dialog = {
        visible: true,
        title: 'Confirmar rechazo',
        message: `¿Estás seguro de que deseas rechazar esta solicitud de ${item.username}?`,
        action: 'reject',
        item: item
      };
    },

    async executeConfirmedAction() {
      const item = this.confirm_dialog.item;
      const action = this.confirm_dialog.action;
      this.confirm_dialog.visible = false;

      try {
        const { sync_command_dispatcher } = await import('@/api/client');

        if (action === 'approve') {
          if (item.solicitud_tipo === 'clock') {
            await sync_command_dispatcher.dispatch('approve_clock_modification', {
              clock_uuid: item.clock_uuid,
              change_index: item.change_index,
              admin_user_id: this.userData.id,
              new_status: 'validated'
            });
          } else if (item.solicitud_tipo === 'absence') {
            await sync_command_dispatcher.dispatch('approve_absence', {
              absence_uuid: item.absence_uuid,
              admin_user_id: this.userData.id,
              new_status: 'approved'
            });
          }
          this.showSnackbar('success', 'Solicitud aprobada correctamente');
        } else if (action === 'reject') {
          if (item.solicitud_tipo === 'clock') {
            await sync_command_dispatcher.dispatch('approve_clock_modification', {
              clock_uuid: item.clock_uuid,
              change_index: item.change_index,
              admin_user_id: this.userData.id,
              new_status: 'rejected'
            });
          } else if (item.solicitud_tipo === 'absence') {
            await sync_command_dispatcher.dispatch('approve_absence', {
              absence_uuid: item.absence_uuid,
              admin_user_id: this.userData.id,
              new_status: 'rejected'
            });
          }
          this.showSnackbar('success', 'Solicitud rechazada');
        }

        await this.loadPendingApprovals();
      } catch (error) {
        console.error('Error executing action:', error);
        this.showSnackbar('error', 'Error al procesar la solicitud');
      }
    },

    openRegistroJornada() {
      this.currentView = 'registro-jornada';
      this.rj_selectedEmployee = null;
      this.rj_clocks = [];
      this.rjSetDefaultDateFilter();
      if (!this.rj_clockRepo) {
        this.rj_clockRepo = new APIClockRepository();
      }
      if (this.rj_selectedPeriod === 'semana') {
        this.rjLoadWeekProgressData();
      } else if (this.rj_selectedPeriod === 'mes') {
        this.rjLoadMonthProgressData();
      } else {
        this.rjLoadProgressData();
      }
    },

    rjSetDefaultDateFilter() {
      this.rj_filter.startDate = '';
      this.rj_filter.endDate = '';
    },

    rjGetFriendshipKindDisplay(kind) {
      const kindMap = {
        'propietario': 'Propietario',
        'admin': 'Administrador',
        'empleado': 'Empleado',
        'cliente': 'Cliente',
      };
      return kindMap[kind] || kind;
    },

    rjSelectEmployee(user) {
      this.rj_selectedEmployee = user;
      this.rjLoadClocks();
      this.rjLoadWeekSummary();
      this.rjLoadMonthSummary();
    },

    rjBackToList() {
      this.rj_selectedEmployee = null;
      this.rj_clocks = [];
      this.rjStopWeekSummaryTimer();
      this.rj_weekSummary = { loading: false, days: [], totalWorkedMinutes: 0, totalExpectedMinutes: 0, schedule: null };
      this.rj_monthSummary = { loading: false, days: [], totalWorkedMinutes: 0, totalExpectedMinutes: 0, schedule: null };
      if (this.rj_selectedPeriod === 'semana') {
        this.rjLoadWeekProgressData();
      } else {
        this.rjLoadProgressData();
      }
    },

    async rjLoadClocks() {
      if (!this.rj_selectedEmployee) return;
      this.rj_loading = true;
      try {
        const criteria = [
          ['user_id', '==', this.rj_selectedEmployee.id]
        ];

        // Use manual filters if set, otherwise auto-filter by selected period
        if (this.rj_filter.startDate) {
          const startDate = new Date(this.rj_filter.startDate);
          startDate.setHours(0, 0, 0, 0);
          criteria.push(['timestamp', '>=', startDate.toISOString()]);
        } else {
          const autoRange = this.rjGetPeriodDateRange();
          if (autoRange.start) {
            criteria.push(['timestamp', '>=', autoRange.start.toISOString()]);
          }
        }
        if (this.rj_filter.endDate) {
          const endDate = new Date(this.rj_filter.endDate);
          endDate.setHours(23, 59, 59, 999);
          criteria.push(['timestamp', '<=', endDate.toISOString()]);
        } else {
          const autoRange = this.rjGetPeriodDateRange();
          if (autoRange.end) {
            criteria.push(['timestamp', '<=', autoRange.end.toISOString()]);
          }
        }

        const clocks = await this.rj_clockRepo.find_by_criteria(
          criteria,
          ['-timestamp'],
          100,
          0,
          true
        );
        this.rj_clocks = clocks;
      } catch (error) {
        console.error('Error loading employee clocks:', error);
        this.rj_snackbar = { visible: true, text: 'Error al cargar los fichajes del empleado', color: 'error' };
      } finally {
        this.rj_loading = false;
      }
    },

    rjGetPeriodDateRange() {
      const today = this.rj_currentDate;
      if (this.rj_selectedPeriod === 'dia') {
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
        const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
        return { start, end };
      }
      if (this.rj_selectedPeriod === 'semana') {
        const dayIdx = (today.getDay() + 6) % 7; // 0=Mon, 6=Sun
        const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - dayIdx, 0, 0, 0, 0);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);
        return { start: monday, end: sunday };
      }
      return { start: null, end: null };
    },

    rjClearFilter() {
      this.rjSetDefaultDateFilter();
      if (this.rj_selectedEmployee) {
        this.rjLoadClocks();
      }
    },

    rjFormatType(type) {
      return formatClockType(type);
    },

    rjGetTypeColor(type) {
      const colors = { 'in': 'green', 'out': 'red', 'pause': 'orange', 'continue': 'blue' };
      return colors[type] || 'grey';
    },

    async rjLoadProgressData() {
      this.rj_progressLoading = true;
      try {
        const { query_dispatcher } = await import('@/api/client');

        const today = this.rj_currentDate;
        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
        const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

        // Fetch ALL company users' today clocks in one call (admin flow without user_id filter)
        const allClocksRaw = await query_dispatcher.dispatch('filter_clocks.query', {
          filters: [
            ['timestamp', '>=', startOfDay.toISOString()],
            ['timestamp', '<=', endOfDay.toISOString()]
          ],
          order_by: ['timestamp'],
          limit: 5000,
          offset: 0,
          powerOrNot: false
        });

        // Group clocks by user_id
        const clocksByUser = {};
        for (const raw of allClocksRaw) {
          const userId = raw.user_info ? raw.user_info.id : raw.user_id;
          if (!userId) continue;
          if (!clocksByUser[userId]) {
            clocksByUser[userId] = [];
          }
          clocksByUser[userId].push({
            uuid: raw.uuid,
            timestamp: new Date(raw.timestamp),
            type: raw.type,
            user: raw.user_info || raw.user_id,
            changes: raw.changes || [],
            sendEmailNotification: false,
            location: raw.location || 'office'
          });
        }

        // Fetch work schedules for ALL users in parallel
        const schedulePromises = this.users.map(user =>
          query_dispatcher.dispatch('get_work_schedule_by_user.query', { user_id: user.id })
            .then(schedule => ({ userId: user.id, schedule }))
            .catch(() => ({ userId: user.id, schedule: null }))
        );
        const scheduleResults = await Promise.all(schedulePromises);
        const schedulesByUser = {};
        for (const result of scheduleResults) {
          schedulesByUser[result.userId] = result.schedule;
        }

        // Build progress data for each user
        const DEFAULT_EXPECTED_SECS = 8 * 60 * 60;
        const dayKeys = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
        const dayKey = dayKeys[today.getDay()];

        const progressData = {};
        for (const user of this.users) {
          const userClocks = clocksByUser[user.id] || [];
          const workedSecs = get_day_secs(userClocks);
          const pauseSecs = get_day_pause_secs(userClocks);

          let expectedSecs = DEFAULT_EXPECTED_SECS;
          const schedule = schedulesByUser[user.id];
          if (schedule && schedule.horario_semanal) {
            const horarioDia = schedule.horario_semanal[dayKey];
            if (horarioDia && horarioDia.hora_entrada && horarioDia.hora_salida) {
              const entradaParts = horarioDia.hora_entrada.split(':');
              const salidaParts = horarioDia.hora_salida.split(':');
              const entradaMinutos = parseInt(entradaParts[0], 10) * 60 + parseInt(entradaParts[1], 10);
              const salidaMinutos = parseInt(salidaParts[0], 10) * 60 + parseInt(salidaParts[1], 10);
              const trabajoMinutos = salidaMinutos - entradaMinutos;
              if (trabajoMinutos > 0) {
                expectedSecs = trabajoMinutos * 60;
              }
            }
          }

          let isActive = false;
          let isPaused = false;
          let horaEntrada = null;
          let horaPausa = null;
          let horaFinPausa = null;
          let horaSalida = null;
          if (userClocks.length > 0) {
            const now = new Date();
            const sorted = [...userClocks].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            // Calcular isActive/isPaused sobre clocks pasados (sin futuros)
            const pastClocks = sorted.filter(c => new Date(c.timestamp).getTime() <= now.getTime());
            const effectiveLastType = pastClocks.length > 0 ? pastClocks[pastClocks.length - 1].type : null;
            isActive = (effectiveLastType === 'in' || effectiveLastType === 'continue');
            isPaused = (effectiveLastType === 'pause');

            const firstIn = sorted.find(c => c.type === 'in');
            if (firstIn) horaEntrada = new Date(firstIn.timestamp);
            const firstPauseIdx = sorted.findIndex(c => c.type === 'pause');
            if (firstPauseIdx !== -1) {
              horaPausa = new Date(sorted[firstPauseIdx].timestamp);
              const continueAfter = sorted.slice(firstPauseIdx + 1).find(c => c.type === 'continue');
              if (continueAfter) horaFinPausa = new Date(continueAfter.timestamp);
            }
            const lastOut = [...sorted].reverse().find(c => c.type === 'out');
            if (lastOut) horaSalida = new Date(lastOut.timestamp);
          }

          progressData[user.id] = {
            clocks: userClocks,
            workedSecs,
            pauseSecs,
            expectedSecs,
            isActive,
            isPaused,
            horaEntrada,
            horaPausa,
            horaFinPausa,
            horaSalida
          };
        }

        this.rj_progressData = progressData;
        this.rjStartProgressTimer();
      } catch (error) {
        console.error('Error loading progress data:', error);
      } finally {
        this.rj_progressLoading = false;
      }
    },

    rjIsViewingToday() {
      const now = new Date();
      const sel = this.rj_currentDate;
      return now.getFullYear() === sel.getFullYear() &&
             now.getMonth() === sel.getMonth() &&
             now.getDate() === sel.getDate();
    },
    rjStartProgressTimer() {
      this.rjStopProgressTimer();
      if (!this.rjIsViewingToday()) return;
      this.rj_progressTimer = setInterval(() => {
        for (const userId of Object.keys(this.rj_progressData)) {
          const data = this.rj_progressData[userId];
          if (data.clocks.length > 0 && (data.isActive || data.isPaused)) {
            this.$set(this.rj_progressData[userId], 'workedSecs', get_day_secs(data.clocks));
            this.$set(this.rj_progressData[userId], 'pauseSecs', get_day_pause_secs(data.clocks));
          }
        }
      }, 1000);
    },

    rjStopProgressTimer() {
      if (this.rj_progressTimer) {
        clearInterval(this.rj_progressTimer);
        this.rj_progressTimer = null;
      }
    },

    async rjLoadWeekProgressData() {
      this.rj_weekProgressLoading = true;
      try {
        const { query_dispatcher } = await import('@/api/client');

        const d = this.rj_currentDate;
        const dayIdx = (d.getDay() + 6) % 7;
        const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dayIdx);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        const startOfWeek = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0, 0);
        const endOfWeek = new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate(), 23, 59, 59, 999);

        const allClocksRaw = await query_dispatcher.dispatch('filter_clocks.query', {
          filters: [
            ['timestamp', '>=', startOfWeek.toISOString()],
            ['timestamp', '<=', endOfWeek.toISOString()]
          ],
          order_by: ['timestamp'],
          limit: 35000,
          offset: 0,
          powerOrNot: false
        });

        const clocksByUser = {};
        for (const raw of allClocksRaw) {
          const userId = raw.user_info ? raw.user_info.id : raw.user_id;
          if (!userId) continue;
          if (!clocksByUser[userId]) clocksByUser[userId] = [];
          clocksByUser[userId].push({
            uuid: raw.uuid,
            timestamp: new Date(raw.timestamp),
            type: raw.type,
            user: raw.user_info || raw.user_id,
            changes: raw.changes || [],
            sendEmailNotification: false,
            location: raw.location || 'office'
          });
        }

        const schedulePromises = this.users.map(user =>
          query_dispatcher.dispatch('get_work_schedule_by_user.query', { user_id: user.id })
            .then(schedule => ({ userId: user.id, schedule }))
            .catch(() => ({ userId: user.id, schedule: null }))
        );
        const scheduleResults = await Promise.all(schedulePromises);
        const schedulesByUser = {};
        for (const result of scheduleResults) {
          schedulesByUser[result.userId] = result.schedule;
        }

        const DEFAULT_EXPECTED_SECS = 8 * 60 * 60;
        const dayKeys = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];

        const progressData = {};
        for (const user of this.users) {
          const userClocks = clocksByUser[user.id] || [];

          let totalWorkedSecs = 0;
          let totalPauseSecs = 0;
          let totalExpectedSecs = 0;
          const dayWorkedSecs = []; 

          for (let i = 0; i < 7; i++) {
            const dayDate = new Date(monday);
            dayDate.setDate(monday.getDate() + i);
            const dayStart = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), 0, 0, 0, 0);
            const dayEnd = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), 23, 59, 59, 999);

            const dayClocks = userClocks.filter(c => {
              const t = c.timestamp.getTime();
              return t >= dayStart.getTime() && t <= dayEnd.getTime();
            });

            const daySecs = get_day_secs(dayClocks);
            dayWorkedSecs.push(daySecs);
            totalWorkedSecs += daySecs;
            totalPauseSecs += get_day_pause_secs(dayClocks);

            const dayKey = dayKeys[dayDate.getDay()];
            const schedule = schedulesByUser[user.id];
            if (schedule && schedule.horario_semanal) {
              const horarioDia = schedule.horario_semanal[dayKey];
              if (horarioDia && horarioDia.hora_entrada && horarioDia.hora_salida) {
                const entradaParts = horarioDia.hora_entrada.split(':');
                const salidaParts = horarioDia.hora_salida.split(':');
                const entradaMinutos = parseInt(entradaParts[0], 10) * 60 + parseInt(entradaParts[1], 10);
                const salidaMinutos = parseInt(salidaParts[0], 10) * 60 + parseInt(salidaParts[1], 10);
                const trabajoMinutos = salidaMinutos - entradaMinutos;
                if (trabajoMinutos > 0) {
                  totalExpectedSecs += trabajoMinutos * 60;
                }
              }
            } else {
              if (dayDate.getDay() !== 0 && dayDate.getDay() !== 6) {
                totalExpectedSecs += DEFAULT_EXPECTED_SECS;
              }
            }
          }

          let isActive = false;
          let isPaused = false;
          let horaEntrada = null;
          let horaPausa = null;
          let horaFinPausa = null;
          let horaSalida = null;

          const now = new Date();
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
          const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          const todayClocks = userClocks.filter(c => {
            const t = c.timestamp.getTime();
            return t >= todayStart.getTime() && t <= todayEnd.getTime();
          });

          if (todayClocks.length > 0) {
            const sorted = [...todayClocks].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
            const pastClocks = sorted.filter(c => c.timestamp.getTime() <= now.getTime());
            const effectiveLastType = pastClocks.length > 0 ? pastClocks[pastClocks.length - 1].type : null;
            isActive = (effectiveLastType === 'in' || effectiveLastType === 'continue');
            isPaused = (effectiveLastType === 'pause');

            const firstIn = sorted.find(c => c.type === 'in');
            if (firstIn) horaEntrada = new Date(firstIn.timestamp);
            const firstPauseIdx = sorted.findIndex(c => c.type === 'pause');
            if (firstPauseIdx !== -1) {
              horaPausa = new Date(sorted[firstPauseIdx].timestamp);
              const continueAfter = sorted.slice(firstPauseIdx + 1).find(c => c.type === 'continue');
              if (continueAfter) horaFinPausa = new Date(continueAfter.timestamp);
            }
            const lastOut = [...sorted].reverse().find(c => c.type === 'out');
            if (lastOut) horaSalida = new Date(lastOut.timestamp);
          }

          progressData[user.id] = {
            clocks: userClocks,
            workedSecs: totalWorkedSecs,
            pauseSecs: totalPauseSecs,
            expectedSecs: totalExpectedSecs,
            dayWorkedSecs,
            isActive,
            isPaused,
            horaEntrada,
            horaPausa,
            horaFinPausa,
            horaSalida
          };
        }

        // === TEST: datos fake para probar 'Estado Cierr' .===
        // const testUserIds = Object.keys(progressData);
        // if (testUserIds.length >= 1) {

        //   // CasoPrimer usuario: horas completadas (40h trabajadas, 40h esperadas)
        //   progressData[testUserIds[0]] = {
        //     ...progressData[testUserIds[0]],
        //     workedSecs: 40 * 3600,
        //     expectedSecs: 40 * 3600,
        //     dayWorkedSecs: [8*3600, 8*3600, 8*3600, 8*3600, 8*3600, 0, 0],
        //   };
        // }
        // if (testUserIds.length >= 2) {
        //   // Segundo usuario: horas incompletas forzado (20h trabajadas, 40h esperadas)
        //   progressData[testUserIds[1]] = {
        //     ...progressData[testUserIds[1]],
        //     workedSecs: 20 * 3600,
        //     expectedSecs: 40 * 3600,
        //     dayWorkedSecs: [4*3600, 4*3600, 4*3600, 4*3600, 4*3600, 0, 0],
        //     _forceIncompleto: true,
        //   };
        // }
        // if (testUserIds.length >= 3) {
        //   // Tercer usuario: horas completadas (40h trabajadas, 40h esperadas)
        //   progressData[testUserIds[2]] = {
        //     ...progressData[testUserIds[2]],
        //     workedSecs: 40 * 3600,
        //     expectedSecs: 40 * 3600,
        //     dayWorkedSecs: [8*3600, 8*3600, 8*3600, 8*3600, 8*3600, 0, 0],
        //   };
        // }
        // === FIN TEST ===

        this.rj_weekProgressData = progressData;
        this.rjStartWeekProgressTimer();
      } catch (error) {
        console.error('Error loading week progress data:', error);
      } finally {
        this.rj_weekProgressLoading = false;
      }
    },

    async rjLoadMonthProgressData() {
      this.rj_monthProgressLoading = true;
      try {
        const { query_dispatcher } = await import('@/api/client');

        const d = this.rj_currentDate;
        const year = d.getFullYear();
        const month = d.getMonth();
        const firstDay = new Date(year, month, 1, 0, 0, 0, 0);
        const lastDay = new Date(year, month + 1, 0, 23, 59, 59, 999);
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const allClocksRaw = await query_dispatcher.dispatch('filter_clocks.query', {
          filters: [
            ['timestamp', '>=', firstDay.toISOString()],
            ['timestamp', '<=', lastDay.toISOString()]
          ],
          order_by: ['timestamp'],
          limit: 100000,
          offset: 0,
          powerOrNot: false
        });

        const clocksByUser = {};
        for (const raw of allClocksRaw) {
          const userId = raw.user_info ? raw.user_info.id : raw.user_id;
          if (!userId) continue;
          if (!clocksByUser[userId]) clocksByUser[userId] = [];
          clocksByUser[userId].push({
            uuid: raw.uuid,
            timestamp: new Date(raw.timestamp),
            type: raw.type,
            user: raw.user_info || raw.user_id,
            changes: raw.changes || [],
            location: raw.location || 'office'
          });
        }

        const schedulePromises = this.users.map(user =>
          query_dispatcher.dispatch('get_work_schedule_by_user.query', { user_id: user.id })
            .then(schedule => ({ userId: user.id, schedule }))
            .catch(() => ({ userId: user.id, schedule: null }))
        );
        const scheduleResults = await Promise.all(schedulePromises);
        const schedulesByUser = {};
        for (const result of scheduleResults) {
          schedulesByUser[result.userId] = result.schedule;
        }

        const DEFAULT_EXPECTED_SECS = 8 * 60 * 60;
        const dayKeys = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];

        const progressData = {};
        for (const user of this.users) {
          const userClocks = clocksByUser[user.id] || [];
          const dayStatuses = [];

          for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
            const dayDate = new Date(year, month, dayNum);
            const dayStart = new Date(year, month, dayNum, 0, 0, 0, 0);
            const dayEnd = new Date(year, month, dayNum, 23, 59, 59, 999);

            const dayClocks = userClocks.filter(c => {
              const t = c.timestamp.getTime();
              return t >= dayStart.getTime() && t <= dayEnd.getTime();
            });

            const workedSecs = get_day_secs(dayClocks);

            let expectedSecs = 0;
            const dayKey = dayKeys[dayDate.getDay()];
            const schedule = schedulesByUser[user.id];
            if (schedule && schedule.horario_semanal) {
              const horarioDia = schedule.horario_semanal[dayKey];
              if (horarioDia && horarioDia.hora_entrada && horarioDia.hora_salida) {
                const entradaParts = horarioDia.hora_entrada.split(':');
                const salidaParts = horarioDia.hora_salida.split(':');
                const entradaMinutos = parseInt(entradaParts[0], 10) * 60 + parseInt(entradaParts[1], 10);
                const salidaMinutos = parseInt(salidaParts[0], 10) * 60 + parseInt(salidaParts[1], 10);
                const trabajoMinutos = salidaMinutos - entradaMinutos;
                if (trabajoMinutos > 0) {
                  expectedSecs = trabajoMinutos * 60;
                }
              }
            } else {
              if (dayDate.getDay() !== 0 && dayDate.getDay() !== 6) {
                expectedSecs = DEFAULT_EXPECTED_SECS;
              }
            }

            const hasClocks = dayClocks.length > 0;
            // 'completed' if worked >= expected and there are expected hours
            // 'incomplete' if has clocks but worked < expected
            // 'no-data' if no clocks and no expected hours, or future day
            let status = 'no-data';
            if (expectedSecs > 0 && hasClocks && workedSecs >= expectedSecs) {
              status = 'completed';
            } else if (hasClocks && expectedSecs > 0 && workedSecs < expectedSecs) {
              status = 'incomplete';
            } else if (!hasClocks && expectedSecs > 0) {
              const today = new Date();
              const isPast = dayDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
              if (isPast) {
                status = 'incomplete';
              }
            }

            dayStatuses.push({
              dayNumber: dayNum,
              workedSecs,
              expectedSecs,
              hasClocks,
              status
            });
          }

          progressData[user.id] = { dayStatuses };
        }

        

        this.rj_monthProgressData = progressData;
      } catch (error) {
        console.error('Error loading month progress data:', error);
      } finally {
        this.rj_monthProgressLoading = false;
      }
    },

    rjGetMonthDayStatus(userId, dayNumber) {
      const userData = this.rj_monthProgressData[userId];
      if (!userData || !userData.dayStatuses) return 'no-data';
      const day = userData.dayStatuses[dayNumber - 1];
      if (!day) return 'no-data';
      return day.status;
    },

    rjGetMonthTotal(userId) {
      const userData = this.rj_monthProgressData[userId];
      if (!userData || !userData.dayStatuses) return { label: '-', deficit: false, completed: false };
      const totalWorkedSecs = userData.dayStatuses.reduce((sum, d) => sum + d.workedSecs, 0);
      const totalExpectedSecs = userData.dayStatuses.reduce((sum, d) => sum + d.expectedSecs, 0);
      const workedH = Math.round(totalWorkedSecs / 3600);
      const expectedH = Math.round(totalExpectedSecs / 3600);
      const d = this.rj_currentDate;
      const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const today = new Date();
      const monthEnded = today > lastDayOfMonth;
      const completed = totalExpectedSecs > 0 && totalWorkedSecs >= totalExpectedSecs;
      const deficit = monthEnded && totalExpectedSecs > 0 && totalWorkedSecs < totalExpectedSecs;
      return { label: `${workedH}h / ${expectedH}h`, deficit, completed };
    },

    async rjLoadYearProgressData() {
      this.rj_yearProgressLoading = true;
      // TODO: implement when data source is defined
      this.rj_yearProgressData = {};
      this.rj_yearProgressLoading = false;
    },

    // ============== In development ==============
    rjGetYearContractLabel(userId) {
      return '-';
    },

    rjGetYearWorkedLabel(userId) {
      return '-';
    },

    rjGetYearBalance(userId) {
      return { secs: 0, formatted: '-' };
    },
    // =============================================
    rjIsViewingCurrentWeek() {
      const now = new Date();
      const d = this.rj_currentDate;
      const dayIdx = (d.getDay() + 6) % 7;
      const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dayIdx);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return now >= monday && now <= sunday;
    },

    rjStartWeekProgressTimer() {
      this.rjStopWeekProgressTimer();
      if (!this.rjIsViewingCurrentWeek()) return;
      this.rj_weekProgressTimer = setInterval(() => {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

        for (const userId of Object.keys(this.rj_weekProgressData)) {
          const data = this.rj_weekProgressData[userId];
          if (data.clocks.length > 0 && (data.isActive || data.isPaused)) {
            const grouped = this.rjGroupClocksByDay(data.clocks);
            let totalWorked = 0;
            let totalPause = 0;
            for (const dayClocks of Object.values(grouped)) {
              totalWorked += get_day_secs(dayClocks);
              totalPause += get_day_pause_secs(dayClocks);
            }
            this.$set(this.rj_weekProgressData[userId], 'workedSecs', totalWorked);
            this.$set(this.rj_weekProgressData[userId], 'pauseSecs', totalPause);
          }
        }
      }, 1000);
    },

    rjStopWeekProgressTimer() {
      if (this.rj_weekProgressTimer) {
        clearInterval(this.rj_weekProgressTimer);
        this.rj_weekProgressTimer = null;
      }
    },

    rjGroupClocksByDay(clocks) {
      const grouped = {};
      for (const c of clocks) {
        const t = c.timestamp;
        const key = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(c);
      }
      return grouped;
    },

    rjGetUserProgress(userId) {
      const data = this.rj_activeProgressData[userId];
      if (!data) {
        return { percentage: 0, workedFormatted: '00:00:00', expectedFormatted: '08:00:00', isActive: false, isCompleted: false };
      }
      const percentage = data.expectedSecs > 0 ? Math.min((data.workedSecs / data.expectedSecs) * 100, 100) : 0;
      const isCompleted = data.expectedSecs > 0 && data.workedSecs >= data.expectedSecs;
      return {
        percentage,
        workedFormatted: formatTime(Math.round(data.workedSecs)),
        expectedFormatted: formatTime(data.expectedSecs),
        isActive: data.isActive,
        isCompleted
      };
    },

    rjGetUserBalance(userId) {
      const data = this.rj_activeProgressData[userId];
      if (!data) {
        return { diffSecs: 0, formatted: '0h 0min' };
      }
      const diffSecs = Math.round(data.workedSecs + data.pauseSecs - data.expectedSecs);
      const absDiff = Math.abs(diffSecs);
      const hours = Math.floor(absDiff / 3600);
      const minutes = Math.floor((absDiff % 3600) / 60);
      const sign = diffSecs > 0 ? '+' : diffSecs < 0 ? '-' : '';
      const formatted = `${sign}${hours}h ${minutes}min`;
      return { diffSecs, formatted };
    },

    rjGetUserProgressSegments(userId) {
      const data = this.rj_activeProgressData[userId];
      if (!data || !data.clocks || data.clocks.length === 0) return [];
      const expectedSecs = data.expectedSecs > 0 ? data.expectedSecs : (8 * 60 * 60);
      return buildProgressSegments(data.clocks, expectedSecs);
    },

    async rjLoadWeekSummary() {
      if (!this.rj_selectedEmployee) return;
      this.rj_weekSummary.loading = true;

      try {
        const { query_dispatcher } = await import('@/api/client');

        const today = new Date();
        const todayDayIdx = (today.getDay() + 6) % 7;
        const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - todayDayIdx);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        const startOfWeek = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0, 0);
        const endOfWeek = new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate(), 23, 59, 59, 999);

        const [weekClocksRaw, schedule] = await Promise.all([
          query_dispatcher.dispatch('filter_clocks.query', {
            filters: [
              ['user_id', '==', this.rj_selectedEmployee.id],
              ['timestamp', '>=', startOfWeek.toISOString()],
              ['timestamp', '<=', endOfWeek.toISOString()]
            ],
            order_by: ['timestamp'],
            limit: 500,
            offset: 0,
            powerOrNot: true
          }),
          query_dispatcher.dispatch('get_work_schedule_by_user.query', {
            user_id: this.rj_selectedEmployee.id
          }).catch(() => null)
        ]);

        const weekClocks = weekClocksRaw.map(raw => ({
          uuid: raw.uuid,
          timestamp: new Date(raw.timestamp),
          type: raw.type,
          user: raw.user_info || raw.user_id,
          changes: raw.changes || [],
          location: raw.location || 'office'
        }));

        const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
        const dayKeyMap = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        const days = [];
        let totalWorked = 0;
        let totalExpected = 0;

        for (let i = 0; i < 7; i++) {
          const dayDate = new Date(monday);
          dayDate.setDate(monday.getDate() + i);
          const dateStr = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
          const dateDisplay = `${String(dayDate.getDate()).padStart(2, '0')}/${String(dayDate.getMonth() + 1).padStart(2, '0')}`;

          const dayClocks = weekClocks.filter(c => {
            const t = c.timestamp;
            const clockDate = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
            return clockDate === dateStr;
          });

          const workedMinutes = this.rjCalculateDayWorkedMinutes(dayClocks, dateStr);

          let expectedMinutes = 0;
          const dayKey = dayKeyMap[i];
          if (schedule && schedule.horario_semanal) {
            const horarioDia = schedule.horario_semanal[dayKey];
            if (horarioDia && horarioDia.hora_entrada && horarioDia.hora_salida) {
              const [eh, em] = horarioDia.hora_entrada.split(':').map(Number);
              const [sh, sm] = horarioDia.hora_salida.split(':').map(Number);
              expectedMinutes = (sh * 60 + sm) - (eh * 60 + em);
              if (expectedMinutes < 0) expectedMinutes = 0;
            }
          }

          const isToday = dateStr === todayStr;
          const isPast = dayDate < new Date(today.getFullYear(), today.getMonth(), today.getDate()) && !isToday;

          days.push({
            dayName: dayNames[i],
            dayNameFull: dayKey,
            dateStr,
            dateDisplay,
            workedMinutes,
            expectedMinutes,
            isToday,
            isPast,
            clocks: dayClocks
          });

          totalWorked += workedMinutes;
          totalExpected += expectedMinutes;
        }

        this.rj_weekSummary = {
          loading: false,
          days,
          totalWorkedMinutes: totalWorked,
          totalExpectedMinutes: totalExpected,
          schedule
        };

        this.rjStartWeekSummaryTimer();
      } catch (error) {
        console.error('Error loading week summary:', error);
        this.rj_weekSummary.loading = false;
      }
    },

    async rjLoadMonthSummary() {
      if (!this.rj_selectedEmployee) return;
      this.rj_monthSummary.loading = true;

      try {
        const { query_dispatcher } = await import('@/api/client');

        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        const startOfMonth = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate(), 0, 0, 0, 0);
        const endOfMonth = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate(), 23, 59, 59, 999);

        const [monthClocksRaw, schedule] = await Promise.all([
          query_dispatcher.dispatch('filter_clocks.query', {
            filters: [
              ['user_id', '==', this.rj_selectedEmployee.id],
              ['timestamp', '>=', startOfMonth.toISOString()],
              ['timestamp', '<=', endOfMonth.toISOString()]
            ],
            order_by: ['timestamp'],
            limit: 2000,
            offset: 0,
            powerOrNot: true
          }),
          query_dispatcher.dispatch('get_work_schedule_by_user.query', {
            user_id: this.rj_selectedEmployee.id
          }).catch(() => null)
        ]);

        const monthClocks = monthClocksRaw.map(raw => ({
          uuid: raw.uuid,
          timestamp: new Date(raw.timestamp),
          type: raw.type,
          user: raw.user_info || raw.user_id,
          changes: raw.changes || [],
          location: raw.location || 'office'
        }));

        const dayNamesShort = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
        const dayKeyMap = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const daysInMonth = lastDay.getDate();

        const days = [];
        let totalWorked = 0;
        let totalExpected = 0;

        for (let d = 1; d <= daysInMonth; d++) {
          const dayDate = new Date(today.getFullYear(), today.getMonth(), d);
          const dateStr = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
          const dateDisplay = `${String(dayDate.getDate()).padStart(2, '0')}/${String(dayDate.getMonth() + 1).padStart(2, '0')}`;
          const jsDay = dayDate.getDay();

          const dayClocks = monthClocks.filter(c => {
            const t = c.timestamp;
            const clockDate = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
            return clockDate === dateStr;
          });

          const workedMinutes = this.rjCalculateDayWorkedMinutes(dayClocks, dateStr);

          let expectedMinutes = 0;
          const dayKey = dayKeyMap[jsDay];
          if (schedule && schedule.horario_semanal) {
            const horarioDia = schedule.horario_semanal[dayKey];
            if (horarioDia && horarioDia.hora_entrada && horarioDia.hora_salida) {
              const [eh, em] = horarioDia.hora_entrada.split(':').map(Number);
              const [sh, sm] = horarioDia.hora_salida.split(':').map(Number);
              expectedMinutes = (sh * 60 + sm) - (eh * 60 + em);
              if (expectedMinutes < 0) expectedMinutes = 0;
            }
          }

          const isToday = dateStr === todayStr;
          const isPast = dayDate < new Date(today.getFullYear(), today.getMonth(), today.getDate()) && !isToday;

          days.push({
            dayName: dayNamesShort[jsDay],
            dayNameFull: dayKey,
            dateStr,
            dateDisplay,
            workedMinutes,
            expectedMinutes,
            isToday,
            isPast,
            clocks: dayClocks
          });

          totalWorked += workedMinutes;
          totalExpected += expectedMinutes;
        }

        this.rj_monthSummary = {
          loading: false,
          days,
          totalWorkedMinutes: totalWorked,
          totalExpectedMinutes: totalExpected,
          schedule
        };
      } catch (error) {
        console.error('Error loading month summary:', error);
        this.rj_monthSummary.loading = false;
      }
    },

    rjCalculateDayWorkedMinutes(dayClocks, dateStr) {
      const sorted = [...dayClocks].sort((a, b) => a.timestamp - b.timestamp);

      const pairs = [];
      let startClock = null;
      for (const clock of sorted) {
        if (clock.type === 'in' || clock.type === 'continue') {
          startClock = clock;
        } else if ((clock.type === 'out' || clock.type === 'pause') && startClock) {
          pairs.push({
            clockIn: startClock.timestamp.toTimeString().slice(0, 5),
            clockOut: clock.timestamp.toTimeString().slice(0, 5)
          });
          startClock = null;
        }
      }
      if (startClock) {
        pairs.push({
          clockIn: startClock.timestamp.toTimeString().slice(0, 5),
          clockOut: '...'
        });
      }

      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const isToday = dateStr === todayStr;

      return pairs.reduce((total, pair) => {
        if (pair.clockOut === '...') {
          if (!isToday) return total;
          const [inH, inM] = pair.clockIn.split(':').map(Number);
          const nowMin = now.getHours() * 60 + now.getMinutes();
          return total + Math.max(0, nowMin - (inH * 60 + inM));
        }
        const [inH, inM] = pair.clockIn.split(':').map(Number);
        const [outH, outM] = pair.clockOut.split(':').map(Number);
        return total + ((outH * 60 + outM) - (inH * 60 + inM));
      }, 0);
    },

    rjStartWeekSummaryTimer() {
      this.rjStopWeekSummaryTimer();
      this.rj_weekSummaryTimer = setInterval(() => {
        // Update weekly summary today entry
        if (this.rj_weekSummary.days) {
          const weekToday = this.rj_weekSummary.days.find(d => d.isToday);
          if (weekToday && weekToday.clocks.length > 0) {
            const newMinutes = this.rjCalculateDayWorkedMinutes(weekToday.clocks, weekToday.dateStr);
            if (newMinutes !== weekToday.workedMinutes) {
              this.$set(weekToday, 'workedMinutes', newMinutes);
              const newTotal = this.rj_weekSummary.days.reduce((sum, d) => sum + d.workedMinutes, 0);
              this.$set(this.rj_weekSummary, 'totalWorkedMinutes', newTotal);
            }
          }
        }
        // Update monthly summary today entry
        if (this.rj_monthSummary.days) {
          const monthToday = this.rj_monthSummary.days.find(d => d.isToday);
          if (monthToday && monthToday.clocks.length > 0) {
            const newMinutes = this.rjCalculateDayWorkedMinutes(monthToday.clocks, monthToday.dateStr);
            if (newMinutes !== monthToday.workedMinutes) {
              this.$set(monthToday, 'workedMinutes', newMinutes);
              const newTotal = this.rj_monthSummary.days.reduce((sum, d) => sum + d.workedMinutes, 0);
              this.$set(this.rj_monthSummary, 'totalWorkedMinutes', newTotal);
            }
          }
        }
      }, 60000);
    },

    rjStopWeekSummaryTimer() {
      if (this.rj_weekSummaryTimer) {
        clearInterval(this.rj_weekSummaryTimer);
        this.rj_weekSummaryTimer = null;
      }
    },

    rjFormatMinutesAsHHMM(minutes) {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return `${h}h ${String(m).padStart(2, '0')}m`;
    },

    rjWeekDayPercentage(day) {
      if (day.expectedMinutes <= 0) return day.workedMinutes > 0 ? 100 : 0;
      return Math.min((day.workedMinutes / day.expectedMinutes) * 100, 100);
    },

    rjOpenEditClock(clock) {
      this.rj_editClockPopup = { show: true, clock: clock, errors: {} };
    },

    rjOpenDayClocks(dayRow) {
      this.rj_dayClockDetail = { show: true, date: dayRow.dateDisplay, clocks: dayRow.clocks };
    },

    rjCloseDayClocks() {
      this.rj_dayClockDetail = { show: false, date: '', clocks: [] };
    },

    rjApplyQuickMessage(value) {
      if (value) {
        this.rj_message.body = value;
      }
    },

    async rjSendMessage() {
      if (!this.rj_message.body || !this.rj_selectedEmployee) return;
      try {
        const { sync_command_dispatcher } = await import('@/api/client');
        await sync_command_dispatcher.dispatch('send.app.notification.to.user', {
          user_id: this.rj_selectedEmployee.id,
          subject: 'Mensaje de administrador',
          message: this.rj_message.body,
          module: 'working_hours',
        });
        this.rj_snackbar = { visible: true, text: 'Mensaje enviado correctamente', color: 'success' };
        this.rj_message = { quickMessage: null, body: '' };
      } catch (error) {
        console.error('Error sending message:', error);
        this.rj_snackbar = { visible: true, text: 'Error al enviar el mensaje', color: 'error' };
      }
    },

    rjOpenEditDayPopup() {
      if (this.rj_checkedUsers.length !== 1) return;
      const userId = this.rj_checkedUsers[0];
      const user = this.users.find(u => u.id === userId);
      if (!user) return;

      const dayInfo = this.rjGetUserDayInfo(userId);

      // Buscar UUIDs originales de los clocks existentes
      const data = this.rj_activeProgressData[userId];
      const clocks = data ? data.clocks : [];
      const sorted = [...clocks].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      const firstIn = sorted.find(c => c.type === 'in');
      const firstPauseIdx = sorted.findIndex(c => c.type === 'pause');
      const firstPause = firstPauseIdx !== -1 ? sorted[firstPauseIdx] : null;
      const continueAfterPause = firstPauseIdx !== -1 ? sorted.slice(firstPauseIdx + 1).find(c => c.type === 'continue') : null;
      const lastOut = [...sorted].reverse().find(c => c.type === 'out');

      this.rj_editDayPopup = {
        show: true,
        loading: false,
        userId: userId,
        userName: user.displayName || user.username,
        entrada: dayInfo.entrada !== '--:--' ? dayInfo.entrada : '',
        pausaInicio: dayInfo.pausaInicio !== '--:--' ? dayInfo.pausaInicio : '',
        pausaFin: dayInfo.pausaFin !== '--:--' ? dayInfo.pausaFin : '',
        salida: dayInfo.salida !== '--:--' ? dayInfo.salida : '',
        entradaMenu: false,
        pausaInicioMenu: false,
        pausaFinMenu: false,
        salidaMenu: false,
        originalEntradaUuid: firstIn ? firstIn.uuid : null,
        originalPausaInicioUuid: firstPause ? firstPause.uuid : null,
        originalPausaFinUuid: continueAfterPause ? continueAfterPause.uuid : null,
        originalSalidaUuid: lastOut ? lastOut.uuid : null
      };
    },

    rjCloseEditDayPopup() {
      this.rj_editDayPopup.show = false;
    },

    async rjSaveEditDayPopup() {
      this.rj_editDayPopup.loading = true;
      try {
        if (!this.rj_clockRepo) {
          this.rj_clockRepo = new APIClockRepository();
        }

        const popup = this.rj_editDayPopup;
        const userId = popup.userId;
        const data = this.rj_activeProgressData[userId];
        const existingClocks = data ? data.clocks : [];

        const d = this.rj_currentDate;
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        const entries = [
          { type: 'in', time: popup.entrada, originalUuid: popup.originalEntradaUuid },
          { type: 'pause', time: popup.pausaInicio, originalUuid: popup.originalPausaInicioUuid },
          { type: 'continue', time: popup.pausaFin, originalUuid: popup.originalPausaFinUuid },
          { type: 'out', time: popup.salida, originalUuid: popup.originalSalidaUuid }
        ];

        for (const entry of entries) {
          if (!entry.time && entry.originalUuid) {
            // Campo vaciado → borrar el clock existente
            await this.rj_clockRepo.deletePauseClock(entry.originalUuid);
          } else if (entry.time && entry.originalUuid) {
            // Campo modificado → actualizar el clock existente
            const existing = existingClocks.find(c => c.uuid === entry.originalUuid);
            if (existing) {
              const timestamp = new Date(`${dateStr}T${entry.time}:00`);
              const updatedClock = new Clock(
                existing.uuid,
                timestamp,
                entry.type,
                existing.user,
                [
                  ...(existing.changes || []),
                  {
                    timestamp: timestamp,
                    type: entry.type,
                    justification: 'Editado por administrador',
                    state: 'validada'
                  }
                ],
                false
              );
              await this.rj_clockRepo.save(updatedClock, {
                companydb_id: this.companydb_id,
                is_admin_or_owner: true
              });
            }
          } else if (entry.time && !entry.originalUuid) {
            // Campo nuevo → crear clock
            const timestamp = new Date(`${dateStr}T${entry.time}:00`);
            await this.rj_clockRepo.create({
              user_id: userId,
              timestamp: timestamp,
              type: entry.type
            });
          }
          // !time && !originalUuid → nada que hacer
        }

        this.rj_snackbar = { visible: true, text: 'Fichajes actualizados correctamente', color: 'success' };
        this.rj_editDayPopup.show = false;
        await this.rjLoadProgressData();
      } catch (error) {
        console.error('Error al guardar fichajes:', error);
        this.rj_snackbar = { visible: true, text: 'Error al guardar los fichajes', color: 'error' };
      } finally {
        this.rj_editDayPopup.loading = false;
      }
    },

    async rjHandleEditClockClose(result) {
      this.rj_editClockPopup.show = false;
      if (result && result.updated_clock) {
        if (!result.justification || result.justification.trim() === '') {
          this.rj_editClockPopup.errors = { justification: 'La justificación es obligatoria' };
          this.rj_editClockPopup.show = true;
          return;
        }
        try {
          const updatedClock = new Clock(
            result.updated_clock.uuid,
            result.updated_clock.timestamp,
            result.updated_clock.type,
            result.updated_clock.user,
            [
              ...(this.rj_editClockPopup.clock.changes || []),
              {
                timestamp: result.updated_clock.timestamp,
                type: result.updated_clock.type,
                justification: result.justification,
                state: 'validada'
              }
            ],
            false
          );
          await this.rj_clockRepo.save(updatedClock, {
            companydb_id: this.companydb_id,
            is_admin_or_owner: true
          });
          this.rj_snackbar = { visible: true, text: 'Fichaje modificado correctamente', color: 'success' };
          await this.rjLoadClocks();
          this.rjLoadWeekSummary();
      this.rjLoadMonthSummary();
        } catch (error) {
          console.error('Error al modificar el fichaje:', error);
          this.rj_snackbar = { visible: true, text: 'Error al modificar el fichaje', color: 'error' };
        }
      }
      this.rj_editClockPopup = { show: false, clock: null, errors: {} };
    },

    onAbsenceReasonsSaved(reasons) {
      this.showSnackbar('success', `Se han guardado ${reasons.length} motivos de ausencia`);
    },

    toggleProfileMenu() {
      this.showProfileMenu = !this.showProfileMenu;
    },

    goToProfile() {
      this.showProfileMenu = false;
      this.$router.push('/profile').catch(() => {});
    },

    goToSettings() {
      this.showProfileMenu = false;
      this.$router.push('/settings').catch(() => {});
    },

    goToMain() {
      this.showProfileMenu = false;
      this.navigateTo('main');
    },

    openSetClockPopUp() {
      const today = new Date();
      this.rj_setClockPopup.form = {
        date: today.toISOString().split('T')[0],
        time: '',
        type: 'in'
      };
      this.rj_setClockPopup.formValid = false;
      this.rj_setClockPopup.loading = false;
      this.rj_setClockPopup.dateMenu = false;
      this.rj_setClockPopup.timeMenu = false;
      this.rj_setClockPopup.show = true;
    },

    async rjSubmitSetClock() {
      if (!this.rj_selectedEmployee || !this.rj_setClockPopup.formValid) return;
      this.rj_setClockPopup.loading = true;
      try {
        const [year, month, day] = this.rj_setClockPopup.form.date.split('-');
        const [hours, minutes] = this.rj_setClockPopup.form.time.split(':');
        const timestamp = new Date(year, month - 1, day, hours, minutes);

        if (!this.rj_clockRepo) {
          this.rj_clockRepo = new APIClockRepository();
        }

        const result = await this.rj_clockRepo.create({
          user_id: this.rj_selectedEmployee.id,
          timestamp: timestamp,
          type: this.rj_setClockPopup.form.type
        });

        if (result && result.output && result.output.error) {
          this.rj_snackbar = { visible: true, text: result.output.error, color: 'error' };
          return;
        }

        this.rj_snackbar = { visible: true, text: 'Fichaje creado correctamente', color: 'success' };
        this.rj_setClockPopup.show = false;
        await this.rjLoadClocks();
        this.rjLoadWeekSummary();
      this.rjLoadMonthSummary();
      } catch (error) {
        console.error('Error al crear fichaje:', error);
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Error al crear el fichaje';
        this.rj_snackbar = { visible: true, text: msg, color: 'error' };
      } finally {
        this.rj_setClockPopup.loading = false;
      }
    }
  }
};
</script>

<style scoped>

.admin-page {
  height: calc(100vh - 60px);
  background-color: #FFFFFF;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 20px 20px 0 0;
}

.admin-header-fixed {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: #FFFFFF;
  flex-shrink: 0;
}

.content-area {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.admin-sidebar {
  width: 220px;
  background: #F2EBFC;
  border-right: 1px solid var(--Neutral-200, #D1D1D1);
  padding: 16px;
  flex-shrink: 0;
  overflow-y: auto;
}

.admin-content {
  flex: 1;
  padding: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.admin-sidebar__section {
  margin-top: 16px;
  margin-bottom: 8px;
}

.admin-sidebar__section-title {
  font-size: 10px;
  font-weight: 500;
  color: #454545;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 12px;
  margin-bottom: 6px;
}


.admin-sidebar__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.admin-sidebar__item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  min-height: 40px;
  padding: 12px 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--Neutral-800, #454545);
  font-family: var(--Static-Label-Large-Font, Roboto);
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: var(--Static-Label-Large-Line-Height, 20px);
  letter-spacing: var(--Static-Label-Large-Tracking, 0.1px);
  text-align: left;
  border-radius: 20px;
  transition: all 0.2s ease;
  position: relative;
}


.admin-sidebar__item:hover {
  background: rgba(123, 91, 228, 0.08);
  color: #7B5BE4;
}

.admin-sidebar__item:hover .admin-sidebar__icon {
  color: #7B5BE4;
}

.admin-sidebar__item:hover .admin-sidebar__svg-icon {
  opacity: 1;
}


.admin-sidebar__item--active {
  background: #E7DBF9;
  color: #66359B;
  font-weight: 500;
}

.admin-sidebar__item--active .admin-sidebar__icon {
  color: #E7DBF9;
}

.admin-sidebar__item--active .admin-sidebar__svg-icon {
  opacity: 1;
  filter: brightness(0) saturate(100%) invert(48%) sepia(45%) saturate(2000%) hue-rotate(236deg) brightness(96%) contrast(85%);
}

.admin-sidebar__item--active:hover {
  background: #DDD0F2;
  color: #66359B;
}

.admin-sidebar__item--child {
  padding-left: 32px;
}


.admin-sidebar__icon {
  font-size: 18px;
  color: #888888;
  flex-shrink: 0;
  transition: color 0.2s ease;
}

/* SVG Icon Styling */
.admin-sidebar__svg-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  opacity: 0.85;
  transition: opacity 0.2s ease;
}


.admin-sidebar__label {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
}


.admin-sidebar__count {
  font-size: 11px;
  font-weight: 400;
  color: inherit;
}


.admin-sidebar__badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 2px 5px;
  background: #FF5A1F;
  color: white;
  font-size: 10px;
  font-weight: 600;
  border-radius: 4px;
  margin-left: auto;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  padding: 12px;
  box-sizing: border-box;
}

.header-buttons {
  display: flex;
  padding: 8px 12px;
  gap: 8px;
  flex-wrap: wrap;
}

.page-title {
  margin: 0;
  margin-left: 20px;
  margin-bottom: 2px;
  font-size: 17px;
  font-weight: 600;
  color: var(--v-primaryDark-base);
}

.right-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Dropdown Mi perfil */
.profile-dropdown {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  background: var(--Primary-700, #F9F6FE);
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  height: auto;
}

/* .profile-dropdown:hover {
  background: #5E2D91;
} */

.profile-dropdown__text {
  color: #66359B;
  font-size: 11px;
  font-weight: 500;
}

.profile-dropdown__arrow {
  color: #66359B !important;
  margin-left: 2px;
}

.profile-dropdown__menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  z-index: 100;
  overflow: hidden;
}

.profile-dropdown__item {
  padding: 12px 16px;
  font-size: 12px;
  color: #4A4A4A;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.profile-dropdown__item:hover {
  background: #F2EBFC;
  color: #7339B7;
}

.btn-administrador {
  display: flex;
  height: auto;
  padding: 10px 18px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  background: var(--Primary-700, #7339B7);
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-administrador:hover {
  background: #5E2D91;
}

.btn-primario.btn-refresh {
  background: var(--Primary-700, #7339B7) !important;
  border: none;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: 14px;
  margin-top: 14px;
}

.btn-primario.btn-refresh:hover {
  background: #7a66d9 !important;
}

.btn-primario.btn-refresh:disabled {
  background: #ccc !important;
  cursor: not-allowed;
}

.icono-btn {
  width: 18px;
  height: 18px;
  margin-right: 0;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
  aspect-ratio: 1/1;
}

.btn-volver {
  display: flex;
  align-items: center;
  color: var(--Neutral-300, #B0B0B0);
  font-family: Roboto;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 14px;
  border-radius: 4px;
  width: 400px;
  height: 16px;
  flex-shrink: 0;
}

.icono-btn-secu {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.header-separator {
  width: 100%;
  border: none;
  border-top: 1px solid #ccc;
  margin: 0;
}

.period-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
}

.period-bar__left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-display {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.period-bar__actions {
  display: flex;
  gap: 8px;
}

.period-selector {
  display: flex;
  border: 1px solid #FFFFFF;
  border-radius: 16px;
  overflow: hidden;
  width: fit-content;
}

.period-selector__btn {
  flex: 1;
  padding: 6px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #66359B;
  background: #E7DBF9;
  border: none;
  border-right: 1px solid #FFFFFF;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.period-selector__btn:last-child {
  border-right: none;
}

.period-selector__btn:hover {
  background: #D4BEF4;
}

.period-selector__btn--active {
  background: #7339B7;
  color: #fff;
}

.period-selector__btn--active:hover {
  background: #6230a0;
}

.date-navigator {
  display: flex;
  align-items: center;
  border: 1px solid var(--Neutral-200, #D1D1D1);
  border-radius: 8px;
  overflow: hidden;
  height: 30px;
}

.date-navigator__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #E7DBF9;
  border: none;
  cursor: pointer;
  padding: 0 6px;
  height: 100%;
  transition: background 0.15s;
}

.date-navigator__arrow:hover {
  background: #DEB1FA;
}

.date-navigator__label {
  padding: 0 14px;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  user-select: none;
}

.title-separator {
  width: 100%;
  border: none;
  border-top: 1px solid #ccc;
  margin: 0;
}

.acciones-secundarias {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  margin: 12px 0;
  padding: 0 12px;
  box-sizing: border-box;
}


.btn-secundario-1 {
  display: flex;
  width: auto;
  height: 26px;
  padding: 6px 12px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  border: 1px solid var(--Neutral-200, #D1D1D1);
  background: var(--Neutral-150, #F3F3F3);
  color: #666;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.btn-secundario-1--pressed {
  background: #E7E7E7;
}

.btn-secundario-1--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-secundario {
  display: flex;
  width: 150px;
  height: 26px;
  padding: 6px 12px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  border: 1px solid var(--Neutral-200, #D1D1D1);
  background: var(--Neutral-150, #E7E7E7);
  color: #333;
  font-size: 11px;
  font-weight: 500;
}

/* New/Adapted styles for the main button */
.btn-primario {
    display: flex;
    height: 26px;
    padding: 6px 12px;
    justify-content: center;
    align-items: center;
    gap: 6px;
    border-radius: 8px;
    background: var(--Primary-700, #7339B7) !important;
    color: white !important;
    font-size: 11px;
    font-weight: 500;
}

.btn-primario[disabled] {
    background: #ccc !important;
    cursor: not-allowed;
}

.data-table-container {
    width: 100%;
}

/* Flat table without elevation */
.flat-table {
    border-radius: 0;
    box-shadow: none !important;
}

.flat-table.v-data-table {
    border-radius: 0;
}

::v-deep .flat-table > .v-data-table__wrapper {
    border-radius: 0;
}

/* Style for inactive users */
::v-deep .inactive-user-row {
    background-color: #f5f5f5 !important;
}

::v-deep .inactive-user-row td {
    color: #9e9e9e !important;
    opacity: 0.7;
}

/* Status chip styling */
.status-chip {
    font-weight: 600;
    min-width: 70px;
    justify-content: center;
}

/* Make sure inactive rows are clearly visible but grayed out */
::v-deep .inactive-user-row .v-chip {
    opacity: 0.8;
}

::v-deep .inactive-user-row .v-icon {
    opacity: 0.5;
}

.subtitle-vicky {
    font-size: 12px;
    font-weight: 400;
    color: #888888;
    text-align: left;
    font-style: italic;
}

/* Reducir tamaño de la tabla */
::v-deep .v-data-table th {
    color: var(--Primary-900, #512A79) !important;
    font-family: Roboto !important;
    font-size: 12px !important;
    font-style: normal !important;
    font-weight: 700 !important;
    line-height: normal !important;
    padding: 8px 12px !important;
    border-right: 1px solid #e0e0e0 !important;
    background-color: #E7DBF9 !important;
}

::v-deep .v-data-table th:last-child {
    border-right: none !important;
}

::v-deep .v-data-table td {
    color: var(--Neutral-600, #565656) !important;
    font-family: Roboto !important;
    font-size: 12px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    line-height: var(--Static-Title-Small-Line-Height, 20px) !important;
    letter-spacing: var(--Static-Title-Small-Tracking, 0.1px) !important;
    padding: 6px 12px !important;
    border-right: 1px solid #e0e0e0 !important;
}

::v-deep .v-data-table td:last-child {
    border-right: none !important;
}

::v-deep .v-data-table .v-chip {
    font-size: 10px !important;
    height: 22px !important;
}

.employee-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.employee-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
}

.approval-actions {
    display: flex;
    gap: 4px;
    justify-content: center;
    padding: 0;
    white-space: nowrap;
}

::v-deep .flat-table td:last-child {
    padding: 0 10px !important;
    width: 1px;
    white-space: nowrap;
}

/* Estilos para la sección de historial */
.history-section {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.history-filters-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 24px;
  width: 100%;
  max-width: 1360px;
  min-height: 56px;
  background: #FFF;
  border: 1px solid var(--Neutral-100, #F3F3F3);
  box-sizing: border-box;
}

.history-filters-bar__left {
  display: flex;
  align-items: center;
}

.history-filters-bar__right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.history-search-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.history-search-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: var(--Primary-950, #351457);
  cursor: pointer;
  transition: all 0.2s ease;
}

.history-search-btn:hover {
  background: #4a1d75;
}

.history-search-btn--active {
  background: #4a1d75;
}

.history-search-btn__icon {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
  transition: opacity 0.2s ease;
}

.history-search-input {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #D1D1D1;
  border-radius: 8px;
  padding: 6px 12px;
  min-width: 250px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.history-search-input:focus-within {
  border-color: #7339B7;
  box-shadow: 0 0 0 2px rgba(115, 57, 183, 0.1);
}

.history-search-input__field {
  border: none;
  outline: none;
  font-size: 13px;
  color: #333;
  width: 100%;
  background: transparent;
}

.history-search-input__field::placeholder {
  color: #999;
}

.history-search-input__clear {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px;
  margin-left: 4px;
  color: #888;
  transition: color 0.2s ease;
}

.history-search-input__clear:hover {
  color: #333;
}

/* Transición de expansión del buscador */
.search-expand-enter-active,
.search-expand-leave-active {
  transition: all 0.25s ease;
}

.search-expand-enter,
.search-expand-leave-to {
  opacity: 0;
  min-width: 0;
  width: 0;
  padding: 0;
  margin-left: -8px;
}

.history-chip {
  cursor: pointer;
  font-size: 13px !important;
  font-weight: 400 !important;
  margin-left: 8px;
}

.history-chip--active {
  background-color: #E8E0F0 !important;
  color: #5E35B1 !important;
}

.history-chip--clear {
  background-color: #F5F5F5 !important;
}

.history-chip__icon {
  width: 20px;
  height: 20px;
  margin-left: 4px;
}

.history-filter-menu {
  min-width: 320px;
  max-height: 400px;
  overflow-y: auto;
}

.history-filter-menu .v-list-item__title {
  font-size: 13px;
}

.btn-reject {
    padding: 8px 8px;
    width: 95px;
    border: 0.5px solid #272727;
    border-radius: 6px;
    background: white;
    color: #272727;
    font-size: 11px;
    font-weight: 850;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-reject:hover {
    background: #FFEBEE;
}

.btn-approve {
    padding: 8px 8px;
    width: 95px;
    border: 1px solid #4CAF50;
    border-radius: 6px;
    background: #D7FFDA;
    color: #2E7D32;
    font-size: 11px;
    font-weight: 850;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-approve:hover {
    background: #C8F7CB;
}

.confirm-popup-header {
    background-color: var(--Primary-700, #7339B7);
    color: white;
    padding: 12px 16px;
}

.confirm-headline {
    color: white !important;
    font-family: Roboto !important;
    font-size: 16px !important;
    font-weight: 600 !important;
}

.confirm-close-btn {
    color: white !important;
}

.confirm-popup-content {
    font-family: Roboto !important;
    font-size: 14px !important;
    color: #666 !important;
    padding: 24px !important;
    text-align: center !important;
}

.confirm-popup-actions {
    padding: 16px !important;
    justify-content: center !important;
}

.btn-confirm-cancel {
    padding: 8px 16px;
    border: 1px solid #D1D1D1;
    border-radius: 6px;
    background: white;
    color: #666;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-right: 8px;
}

.btn-confirm-cancel:hover {
    background: #f5f5f5;
}

.btn-confirm-approve {
    padding: 8px 16px;
    border: 1px solid #4CAF50;
    border-radius: 6px;
    background: #D7FFDA;
    color: #2E7D32;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-confirm-approve:hover {
    background: #C8F7CB;
}

.btn-confirm-reject {
    padding: 8px 16px;
    border: 1px solid #D32F2F;
    border-radius: 6px;
    background: #FFEBEE;
    color: #D32F2F;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-confirm-reject:hover {
    background: #FFCDD2;
}

/* Registro de Jornada - Vista inline */
.registro-jornada-view {
    padding: 0;
    width: 100%;
}

.rj-subtitle {
    color: #666;
    margin-bottom: 24px;
    font-size: 14px;
}

.rj-employees-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.rj-employee-card {
    display: flex;
    align-items: center;
    padding: 16px;
    background: #f9f9f9;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    cursor: pointer;
    transition: all 0.2s ease;
}

.rj-employee-card:hover {
    background: #f0f0f0;
    border-color: var(--Primary-700, #7339B7);
    transform: translateX(4px);
}

.rj-employee-icon {
    font-size: 48px !important;
    margin-right: 16px;
}

.rj-employee-info {
    flex: 1;
}

.rj-employee-name-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.rj-employee-name {
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.rj-employee-email {
    font-size: 13px;
    color: #666;
}

.rj-employee-role {
    font-size: 12px;
    color: #888;
    margin-top: 4px;
}

/* Tabla Registro de Jornada */
.registro-jornada-view .rj-employees-list {
    margin: 0;
    padding: 0;
}

::v-deep .rj-table.v-data-table {
    margin-top: 0;
}

::v-deep .rj-table tr {
    cursor: pointer;
}

::v-deep .rj-table tr:hover {
    background: #f5f0fc !important;
}

.rj-table-employee {
    display: flex;
    align-items: center;
}

.rj-table-checkbox {
    margin-right: 8px;
    flex-shrink: 0;
}

.rj-table ::v-deep .rj-header-row .rj-header-cell {
    font-size: 13px !important;
    font-weight: 600 !important;
    padding: 10px 12px !important;
    white-space: nowrap;
    position: relative;
}

.rj-table ::v-deep .rj-header-cell--bordered {
    box-shadow: inset -1px 0 0 0 #E0E0E0 !important;
}

.rj-table ::v-deep .rj-header-cell--empleado {
    width: 250px;
}

.rj-table ::v-deep .rj-subheader-row {
    background: #FFFFFF !important;
}

.rj-table ::v-deep .rj-subheader-row th {
    background: #FFFFFF !important;
    color: var(--Primary-700, #7339B7) !important;
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.12px;
    padding: 4px 12px;
    white-space: nowrap;
    height: 28px;
}

.rj-subheader-empleado-icons {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.rj-subheader-icon {
    width: 20px;
    height: 20px;
    padding: 4px;
    border-radius: 4px;
    cursor: pointer;
    box-sizing: content-box;
    transition: background-color 0.2s ease;
}

.rj-subheader-icon:hover {
    background-color: #EBEBEB;
}

.rj-subheader-icon--active {
    background-color: #EBEBEB;
}

/* ── Mes table styles ── */
.rj-employees-list--mes {
    overflow-x: auto;
}

.rj-table--mes ::v-deep table {
    table-layout: auto;
}

.rj-table--mes ::v-deep .rj-header-cell--empleado,
.rj-table--mes ::v-deep .rj-mes-sticky-col--empleado {
    width: 200px;
    min-width: 200px;
    max-width: 200px;
}

.rj-table--mes ::v-deep .rj-mes-sticky-col--estado {
    width: 100px;
    min-width: 100px;
    max-width: 100px;
}

.rj-mes-day-header {
    padding: 2px 0 !important;
    min-width: 32px;
    width: 32px;
    max-width: 32px;
}

.rj-mes-summary-header {
    min-width: 90px;
    white-space: nowrap;
}

.rj-mes-day-header--weekend {
    background-color: #F9F5FF !important;
}

.rj-mes-day-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.1;
}

.rj-mes-day-number {
    font-size: 10px;
    font-weight: 600;
    color: #565656;
}

.rj-mes-day-letter {
    font-size: 10px;
    font-weight: 500;
    color: var(--Primary-700, #7339B7);
}

.rj-mes-day-header--weekend .rj-mes-day-number,
.rj-mes-day-header--weekend .rj-mes-day-letter {
    color: #9E9E9E;
}

.rj-mes-cell {
    white-space: nowrap;
    padding: 8px 4px !important;
    font-size: 12px;
}

.rj-mes-cell--empleado {
    padding: 8px 12px !important;
}

.rj-mes-day-cell {
    min-width: 32px;
    width: 32px;
    max-width: 32px;
    height: 32px;
    padding: 0 !important;
    line-height: 32px;
}

.rj-mes-day-cell--weekend {
    background-color: #FAFAFA;
}

.rj-mes-day-value {
    font-size: 11px;
    color: #757575;
}

.rj-mes-total--deficit {
    color: #A70D0D !important;
}

.rj-mes-estado-cierre {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
}

.rj-mes-estado-cierre__icon {
    width: 14px;
    height: 14px;
}

.rj-mes-estado-cierre--correcto {
    background-color: #D7FFDA;
    color: #1B6E20;
}

.rj-mes-estado-cierre--incorrecto {
    background-color: #F8E0E2;
    color: #A70D0D;
}

.rj-mes-day-icon {
    width: 14px;
    height: 14px;
    vertical-align: middle;
    display: inline-block;
    margin-left: -6px;
}

.rj-table ::v-deep td {
    white-space: nowrap;
}

.rj-table ::v-deep td:nth-child(1),
.rj-table ::v-deep td:nth-child(2),
.rj-table ::v-deep td:nth-child(3),
.rj-table ::v-deep td:nth-child(4),
.rj-table ::v-deep td:nth-child(5),
.rj-table ::v-deep td:nth-child(6),
.rj-table ::v-deep td:nth-child(7),
.rj-table ::v-deep td:nth-child(8),
.rj-table ::v-deep td:nth-child(9),
.rj-table ::v-deep td:nth-child(10),
.rj-table ::v-deep td:nth-child(11),
.rj-table ::v-deep td:nth-child(12) {
    width: 1%;
    padding: 0 16px !important;
}

.rj-balance-positive,
.rj-balance-negative,
.rj-balance-zero {
    color: var(--Neutral-600, #565656) !important;
    text-align: center;
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: var(--Static-Title-Small-Line-Height, 20px);
    letter-spacing: var(--Static-Title-Small-Tracking, 0.1px);
}

.rj-acciones-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.rj-acciones-icon {
    width: 24px;
    height: 24px;
}

.rj-acciones-gestionar-btn {
    text-transform: none !important;
    font-weight: 600;
    letter-spacing: 0.3px;
    border-radius: 8px !important;
    border: 1px solid var(--Neutral-300, #B0B0B0) !important;
    background: var(--White, #FFF) !important;
    color: #565656 !important;
    box-shadow: none !important;
}

.rj-cell-value {
    color: var(--Neutral-600, #565656);
    text-align: center;
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: var(--Static-Title-Small-Line-Height, 20px);
    letter-spacing: var(--Static-Title-Small-Tracking, 0.1px);
}

.rj-status-chip {
    font-weight: 600;
    justify-content: center;
}

.rj-status-chip--completed {
    color: var(--Neutral-600, #565656) !important;
    text-align: center;
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: var(--Static-Title-Small-Line-Height, 20px);
    letter-spacing: var(--Static-Title-Small-Tracking, 0.1px);
}

.rj-status-chip__svg {
    width: 14px;
    height: 14px;
    margin-right: 6px;
}

.rj-estado-cierre {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    border-radius: 12px;
    color: var(--Neutral-600, #565656);
    text-align: center;
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: var(--Static-Title-Small-Line-Height, 20px);
    letter-spacing: var(--Static-Title-Small-Tracking, 0.1px);
    white-space: nowrap;
}

.rj-estado-cierre__icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
}

.rj-table-balance {
    display: flex;
    align-items: center;
    gap: 0;
}

.rj-table-balance__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 70px;
    flex-shrink: 0;
}

.rj-table-balance__item--pausa {
    width: 110px;
}

.rj-table-balance__label {
    font-size: 9px;
    color: #999;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    line-height: 1;
}

.rj-table-balance__value {
    font-size: 12px;
    font-weight: 700;
    color: #333;
}

.rj-table-balance__item--total .rj-table-balance__value {
    color: #7339B7;
}

.rj-table-balance__progress {
    display: flex;
    align-items: center;
    margin: 0;
}

.rj-employee-history {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
    min-height: 0;
    flex: 1;
}

/* ── Detail header (estilo Dia) ── */
.rj-detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #E7DBF9;
    padding: 10px 16px;
    min-height: 44px;
}

.rj-detail-header__left {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    transition: opacity 0.15s;
    flex-shrink: 0;
}

.rj-detail-header__left:hover {
    opacity: 0.7;
}

.rj-detail-header__arrow {
    color: #66359B !important;
    font-size: 16px !important;
}

.rj-detail-header__back-text {
    font-size: 13px;
    font-weight: 600;
    color: #66359B;
    letter-spacing: 0.3px;
    white-space: nowrap;
}

.rj-detail-header__center {
    flex: 1;
    text-align: center;
}

.rj-detail-header__username {
    font-size: 16px;
    font-weight: 600;
    color: #3B1A6E;
}

.rj-detail-header__right {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
}

.rj-detail-header__filter-chip {
    cursor: pointer;
    font-size: 12px !important;
    font-weight: 600;
    height: 28px !important;
}

.rj-filter-menu {
    background: white;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 260px;
}

.rj-filter-menu__row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.rj-filter-menu__label {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    min-width: 50px;
}

.rj-filter-menu__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
}

.rj-detail-header__crear-btn {
    text-transform: none;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0;
}

.rj-active-filters {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    background: #F5F0FB;
}

.rj-active-filters__chip {
    font-size: 12px !important;
}

/* ── Detail subheader ── */
.rj-detail-subheader {
    display: flex;
    align-items: center;
    background: #FFFFFF;
    border-bottom: 1px solid #E0E0E0;
    padding: 6px 16px;
    min-height: 32px;
}

.rj-detail-subheader__col {
    flex: 1;
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: #7339B7;
    letter-spacing: -0.12px;
    white-space: nowrap;
}

.rj-back-btn {
    background: #f0f0f0 !important;
}

.rj-selected-info {
    display: flex;
    flex-direction: column;
}

.rj-selected-info .rj-employee-name {
    font-size: 18px;
    font-weight: 600;
}

.rj-selected-info .rj-employee-email {
    font-size: 13px;
    color: #666;
}
.rj-summaries-row {
    display: flex;
    gap: 16px;
    padding: 16px 24px 0 24px;
}

.rj-week-summary {
    flex: 1;
    min-width: 0;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 16px;
}

.rj-month-days-grid {
    max-height: 280px;
    overflow-y: auto;
}

@media (max-width: 768px) {
    .rj-summaries-row {
        flex-direction: column;
    }
}

.rj-week-summary-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.rj-week-summary-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    flex: 1;
}

.rj-week-summary-total {
    font-size: 13px;
    font-weight: 600;
    color: #7339B7;
}

.rj-week-summary-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    padding: 16px 0;
    font-size: 13px;
    color: #888;
}

.rj-week-days-grid {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.rj-week-day-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-radius: 8px;
    transition: background 0.15s ease;
}

.rj-week-day-today {
    background: #F3EAFF;
    border: 1px solid #D4BEF0;
}

.rj-week-day-past {
    opacity: 0.85;
}

.rj-week-day-future {
    opacity: 0.5;
}

.rj-week-day-label {
    display: flex;
    flex-direction: column;
    min-width: 40px;
    align-items: center;
}

.rj-week-day-name {
    font-size: 12px;
    font-weight: 600;
    color: #333;
    text-transform: uppercase;
}

.rj-week-day-date {
    font-size: 10px;
    color: #888;
}

.rj-week-day-bar-container {
    flex: 1;
}

.rj-week-day-bar-track {
    width: 100%;
    height: 12px;
    background: #E7E7E7;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #D0D0D0;
    box-sizing: border-box;
}

.rj-week-day-bar-fill {
    height: 100%;
    border-radius: 6px 0 0 6px;
    transition: width 0.3s ease;
}

.rj-week-day-bar-complete {
    background: #7DD985;
    border-radius: 6px;
}

.rj-week-day-bar-active {
    background: #D4BEF0;
}

.rj-week-day-bar-deficit {
    background: #FFB74D;
}

.rj-week-day-hours {
    min-width: 110px;
    text-align: right;
    font-size: 12px;
    font-weight: 500;
    color: #333;
}

.rj-week-day-expected {
    color: #888;
}

.rj-week-total-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 10px 0;
    margin-top: 8px;
    border-top: 1px solid #e0e0e0;
}

.rj-week-total-label {
    min-width: 40px;
    font-size: 13px;
    font-weight: 700;
    color: #333;
    text-align: center;
}

.rj-week-total-bar-container {
    flex: 1;
}

.rj-week-total-bar-track {
    height: 14px;
    border-radius: 7px;
}

.rj-week-total-hours {
    min-width: 110px;
    text-align: right;
    font-size: 13px;
    font-weight: 600;
    color: #7339B7;
}

.rj-filter-section {
    background: #f5f5f5;
    padding: 16px;
    border-radius: 8px;
    margin: 16px 24px 0 24px;
}

.rj-filter-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.rj-filter-label {
    font-weight: 600;
    font-size: 14px;
    color: #333;
}

.rj-date-input {
    padding: 8px 12px;
    border: 1px solid #D1D1D1;
    border-radius: 8px;
    font-size: 14px;
    background: white;
}

.rj-clocks-table {
    margin: 0;
    min-height: 370px;
}
::v-deep .rj-clocks-table tbody tr td {
    border-bottom: 1px solid #E0E0E0 !important;
}

.rj-cell-date {
    display: flex;
    align-items: center;
    gap: 16px;
    white-space: nowrap;
}

.rj-cell-date__day {
    font-weight: 700;
    font-size: 12px;
    color: #333;
    min-width: 75px;
}

.rj-cell-date__num {
    font-weight: 600;
    font-size: 12px;
    color: #565656;
}

.rj-no-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    color: #999;
}

.rj-no-data p {
    margin-top: 16px;
    text-align: center;
}

.rj-pagination {
    display: flex;
    justify-content: center;
    padding: 16px 24px 0 24px;
    border-top: 1px solid #e0e0e0;
}

.rj-pagination-info {
    font-size: 13px;
    color: #666;
}

.rj-popup-header {
    background-color: var(--Primary-700, #7339B7);
    padding: 12px 16px;
}

.rj-form-field {
    margin-bottom: 8px;
}

.rj-form-label {
    font-weight: 600;
    font-size: 13px;
    color: #333;
    margin-bottom: 4px;
    display: block;
}

/* Progress bar in employee cards */
.rj-progress-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
}

.rj-progress-loading-text {
    font-size: 13px;
    color: #888;
}

.rj-employee-progress {
    display: flex;
    align-items: center;
    gap: 6px;
}

.rj-progress-bar-track {
    width: 202px;
    height: 12px;
    background: #E7E7E7;
    border-radius: 7px;
    overflow: hidden;
    position: relative;
    border: 1px solid #D0D0D0;
    box-sizing: border-box;
}

.rj-progress-bar-segment {
    height: 100%;
    position: absolute;
    top: 0;
    transition: width 0.3s ease;
}

.rj-segment-work {
    background: #E7DBF9;
    z-index: 2;
}

.rj-segment-pause {
    background: #FAF9D7;
    z-index: 2;
}

.rj-segment-work-completed {
    background: #7DD985;
    z-index: 2;
}

.rj-segment-pause-completed {
    background: #FFFDC5;
    z-index: 2;
}

.rj-segment-active {
    animation: rj-pulse 2s ease-in-out infinite;
}

@keyframes rj-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
}

.rj-progress-text {
    font-size: 11px;
    font-weight: 500;
    color: #888;
    white-space: nowrap;
}

@media (max-width: 600px) {
    .rj-filter-row {
        flex-direction: column;
        align-items: stretch;
    }

    .rj-date-input {
        width: 100%;
    }

    .rj-progress-bar-track {
        width: 80px;
    }
}

.rj-edit-day-card {
    display: flex;
    flex-direction: column;
}

.rj-edit-day-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px 24px;
}

.rj-edit-day-cell .campo-label {
    margin-bottom: 6px;
}

.rj-edit-day-field {
    display: flex;
    align-items: center;
    gap: 4px;
}

.rj-edit-day-field .v-text-field {
    flex: 1;
}

.rj-edit-day-delete {
    flex-shrink: 0;
    width: 28px !important;
    min-width: 28px !important;
    height: 28px !important;
}

.rj-edit-day-delete--hidden {
    visibility: hidden;
}

/* ── Messages Panel (bottom) ── */
.rj-messages-panel {
    margin-top: 32px;
    width: 100%;
}

.rj-messages-panel__header {
    display: flex;
    align-items: baseline;
    gap: 16px;
    background: #F3F3F3;
    padding: 10px 24px;
    border-top: 2px solid #E0E0E0;
}

.rj-messages-panel__title {
    color: var(--Neutral-500, #6D6D6D);
    font-family: Roboto;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
}

.rj-messages-panel__date {
    color: var(--Neutral-400, #888);
    text-align: center;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: var(--Static-Title-Small-Line-Height, 20px);
    letter-spacing: var(--Static-Title-Small-Tracking, 0.1px);
}

.rj-messages-panel__body {
    display: flex;
    gap: 24px;
    background: #FFFFFF;
    padding: 16px 24px;
}

.rj-messages-panel__col-left {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.rj-messages-panel__col-right {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
}

.rj-messages-panel__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.rj-messages-panel__field--full {
    flex: 1;
}

.rj-messages-panel__label {
    font-size: 12px;
    font-weight: 600;
    color: #555;
}

.rj-messages-panel__input {
    font-size: 13px;
}

.rj-messages-panel__textarea {
    font-size: 13px;
}
</style>
